import React, { useState, useEffect, useRef } from 'react';
// import { useTheme } from '../contexts/ThemeContext';
import UserService from '../services/userService';
import AuthService from '../services/authService';
import AuthServiceV2 from '../services/authServiceV2';
import QuizService from '../services/quizService';
import AppSettingsService from '../services/appSettingsService';
import reviewerService from '../services/reviewerService';
import userResultsService from '../services/userResultsService';
import toastService from '../services/toastService';
import { API_CONFIG, getApiUrl, formatEndpoint } from '../config/api';
import { AUTH_ROLES } from '../utils/authConstants';
import {
  formatDate,
  formatDateTime as formatUtcDateTime,
  formatDuration,
  localDateTimeInputToUtcIso,
  minutesToSeconds,
  secondsToMinutes,
  utcIsoToLocalDateTimeInput
} from '../utils/timeUtils';
import axios from 'axios';
import DefaultQuizBanner from '../assets/Quiz_Banner.png';
// import ThemeToggle from './ThemeToggle';
import DeleteConfirm from './DeleteConfirm';
import './AdminDashboard.css';

const EMPTY_QUIZ_INSTRUCTIONS = {
  title: '',
  estimated_time_label: 'Estimated Time:',
  estimated_time_value: '1.45 mins',
  total_marks_label: 'Total Marks:',
  total_marks_value: '100',
  breakdown_label: 'Breakdown:',
  breakdown_text: '',
  intro_text: '',
  agreement_text: '',
  error_text: '',
  sections: []
};

const createInstructionSection = () => ({
  title: '',
  body: '',
  bullets: [],
  variant: 'normal'
});

const SECURITY_RULE_META = {
  tabSwitch: {
    label: 'Tab switch or minimize',
    description: 'Detects when the browser tab is hidden or minimized.'
  },
  windowBlur: {
    label: 'Window lost focus',
    description: 'Detects when the exam window loses focus.'
  },
  rightClick: {
    label: 'Right click',
    description: 'Blocks and counts context-menu attempts.'
  },
  copyAttempt: {
    label: 'Copy attempt',
    description: 'Blocks and counts copy actions.'
  },
  pasteAttempt: {
    label: 'Paste attempt',
    description: 'Blocks and counts paste actions.'
  },
  devTools: {
    label: 'Developer tools',
    description: 'Detects devtools size changes and common shortcuts.'
  },
  suspiciousTyping: {
    label: 'Suspicious typing speed',
    description: 'Counts unusually fast typing in answer inputs.'
  }
};

const DEFAULT_SECURITY_SETTINGS = {
  autoSubmit: true,
  rules: Object.keys(SECURITY_RULE_META).reduce((acc, key) => {
    acc[key] = {
      enabled: true,
      max: key === 'devTools' ? 10 : ['tabSwitch', 'windowBlur', 'suspiciousTyping'].includes(key) ? 3 : 5
    };
    return acc;
  }, {}),
  suspiciousTyping: {
    charsPerSecond: 100,
    minChars: 10
  }
};

const DEFAULT_SESSION_POLICY = {
  resume_enabled: true,
  max_reconnects: 3,
  flag_after_reconnect_limit: true,
  auto_submit_on_reconnect_limit: false,
  auto_submit_on_expiry: true
};

const normalizeInstructionForm = (data = {}) => ({
  ...EMPTY_QUIZ_INSTRUCTIONS,
  ...data,
  sections: Array.isArray(data.sections)
    ? data.sections.map((section) => ({
      ...createInstructionSection(),
      ...section,
      bullets: Array.isArray(section.bullets) ? section.bullets : []
    }))
    : []
});

const normalizeSecurityForm = (data = {}) => {
  const incomingRules = data.rules || {};
  return {
    ...DEFAULT_SECURITY_SETTINGS,
    ...data,
    rules: Object.keys(SECURITY_RULE_META).reduce((acc, key) => {
      const defaultRule = DEFAULT_SECURITY_SETTINGS.rules[key];
      const incomingRule = incomingRules[key] || {};
      acc[key] = {
        enabled: typeof incomingRule.enabled === 'boolean' ? incomingRule.enabled : defaultRule.enabled,
        max: Number.isFinite(Number(incomingRule.max)) ? Number(incomingRule.max) : defaultRule.max
      };
      return acc;
    }, {}),
    suspiciousTyping: {
      ...DEFAULT_SECURITY_SETTINGS.suspiciousTyping,
      ...(data.suspiciousTyping || {})
    }
  };
};

const normalizeSessionPolicy = (data = {}) => ({
  ...DEFAULT_SESSION_POLICY,
  ...data,
  resume_enabled: typeof data.resume_enabled === 'boolean'
    ? data.resume_enabled
    : DEFAULT_SESSION_POLICY.resume_enabled,
  max_reconnects: Number.isFinite(Number(data.max_reconnects))
    ? Math.max(0, Number(data.max_reconnects))
    : DEFAULT_SESSION_POLICY.max_reconnects,
  flag_after_reconnect_limit: typeof data.flag_after_reconnect_limit === 'boolean'
    ? data.flag_after_reconnect_limit
    : DEFAULT_SESSION_POLICY.flag_after_reconnect_limit,
  auto_submit_on_reconnect_limit: typeof data.auto_submit_on_reconnect_limit === 'boolean'
    ? data.auto_submit_on_reconnect_limit
    : DEFAULT_SESSION_POLICY.auto_submit_on_reconnect_limit,
  auto_submit_on_expiry: typeof data.auto_submit_on_expiry === 'boolean'
    ? data.auto_submit_on_expiry
    : DEFAULT_SESSION_POLICY.auto_submit_on_expiry
});

const AdminDashboard = ({ onLogout }) => {
  // Refs to prevent duplicate API calls in React StrictMode
  const didFetchQuizzes = useRef(false);
  const didFetchQuizzesWithQuestions = useRef(false);
  const loginBannerInputRef = useRef(null);
  const questionImageInputRef = useRef(null);
  const optionImageInputRefs = useRef({});

  // const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [userListType, setUserListType] = useState('all'); // 'all', 'users', 'admins'
  const [designations, setDesignations] = useState([]);
  const [quizData, setQuizData] = useState([]);
  const [showDataUpload, setShowDataUpload] = useState(false);
  
  // Dashboard stats states
  const [totalDesignations, setTotalDesignations] = useState(0);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  
  // User Management states
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userType, setUserType] = useState('user'); // 'admin' or 'user'
  const [showAdminForm, setShowAdminForm] = useState(false); // Separate admin form
  const [showRegularUserForm, setShowRegularUserForm] = useState(false); // Separate user form
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false); // Bulk upload modal
  const [bulkUploadFile, setBulkUploadFile] = useState(null); // Selected file for bulk upload
  const [isUploading, setIsUploading] = useState(false); // Upload loading state
  const [showCreateDropdown, setShowCreateDropdown] = useState(false); // Create dropdown state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    avatar: '',
    address: '',
    designationId: '',
    eventId: '',
    status: true
  });

  const [designationForm, setDesignationForm] = useState({
    name: '',
    description: ''
  });
  const [showDesignationForm, setShowDesignationForm] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState(null);
  
  // Events states
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [eventForm, setEventForm] = useState({
    name: '',
    description: ''
  });
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Dropdown options for user forms
  const [availableDesignations, setAvailableDesignations] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  
  // Quiz Builder states
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesWithQuestions, setQuizzesWithQuestions] = useState([]);
  const [expandedQuizzes, setExpandedQuizzes] = useState(new Set());
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizModalMode, setQuizModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingQuizzesWithQuestions, setLoadingQuizzesWithQuestions] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showQuestionsView, setShowQuestionsView] = useState(false);
  
  // User pagination states
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userItemsPerPage, setUserItemsPerPage] = useState(20);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userTotalCount, setUserTotalCount] = useState(0);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    entityType: '',
    entityLabel: '',
    impactWarnings: [],
    onConfirm: null,
    isDeleting: false
  });
  
  // Edit question states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Quiz form states
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    total_time: 60,
    eventId: null
  });

  // Quiz events dropdown options
  const [availableQuizEvents, setAvailableQuizEvents] = useState([]);

  // Reviewer states
  const [reviewerEvents, setReviewerEvents] = useState([]);
  const [reviewerQuizzes, setReviewerQuizzes] = useState([]);
  const [reviewerUsers, setReviewerUsers] = useState([]);
  const [reviewerUsersPage, setReviewerUsersPage] = useState(1);
  const [reviewerUsersLimit, setReviewerUsersLimit] = useState(20);
  const [reviewerUsersTotalPages, setReviewerUsersTotalPages] = useState(1);
  const [reviewerUsersTotalCount, setReviewerUsersTotalCount] = useState(0);
  const [reviewerUsersDesignationFilter, setReviewerUsersDesignationFilter] = useState('all');
  const [reviewerAnswers, setReviewerAnswers] = useState([]);
  const [reviewerLoading, setReviewerLoading] = useState(false);
  const [reviewerError, setReviewerError] = useState(null);
  const [selectedReviewerEvent, setSelectedReviewerEvent] = useState(null);
  const [selectedReviewerQuiz, setSelectedReviewerQuiz] = useState(null);
  const [selectedReviewerUser, setSelectedReviewerUser] = useState(null);
  const [reviewerView, setReviewerView] = useState('events'); // 'events', 'quizzes', 'users', 'answers'
  const [answerFilter, setAnswerFilter] = useState('all'); // 'all', 'pending', 'graded'
  const [exporting, setExporting] = useState(false);
  
  // Grading states
  const [gradingAnswers, setGradingAnswers] = useState({}); // Track which answers are being graded
  const [gradingInputs, setGradingInputs] = useState({}); // Track grading input values
  const [gradingNotes, setGradingNotes] = useState({}); // Track reviewer notes
  
  // Compiler states
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState({}); // Track selected language per answer
  
  // User Results states
  const [userResultsEvents, setUserResultsEvents] = useState([]);
  const [selectedUserResultsEvent, setSelectedUserResultsEvent] = useState(null);
  const [userResultsLeaderboard, setUserResultsLeaderboard] = useState([]);
  const [userResultsLoading, setUserResultsLoading] = useState(false);
  const [userResultsError, setUserResultsError] = useState(null);
  
  // Pagination states for user results
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLimit, setEventsLimit] = useState(20);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const [leaderboardTotal, setLeaderboardTotal] = useState(0);
  const [leaderboardLimit, setLeaderboardLimit] = useState(20);

  // Branding states
  const [loginBannerUrl, setLoginBannerUrl] = useState('');
  const [loginBannerFile, setLoginBannerFile] = useState(null);
  const [loginBannerPreview, setLoginBannerPreview] = useState('');
  const [loginBannerLoading, setLoginBannerLoading] = useState(false);
  const [loginBannerSaving, setLoginBannerSaving] = useState(false);

  // Quiz instruction settings states
  const [quizInstructions, setQuizInstructions] = useState(null);
  const [quizInstructionsLoading, setQuizInstructionsLoading] = useState(false);
  const [quizInstructionsSaving, setQuizInstructionsSaving] = useState(false);

  // Security settings states
  const [securitySettings, setSecuritySettings] = useState(null);
  const [securitySettingsLoading, setSecuritySettingsLoading] = useState(false);
  const [securitySettingsSaving, setSecuritySettingsSaving] = useState(false);
  const [sessionPolicy, setSessionPolicy] = useState(null);
  const [sessionPolicyLoading, setSessionPolicyLoading] = useState(false);

  // Bulk upload functions
  const handleBulkUpload = async () => {
    console.log('handleBulkUpload called');
    console.log('bulkUploadFile:', bulkUploadFile);
    
    if (!bulkUploadFile) {
      console.log('No file selected');
      toastService.error('Please select an Excel (.xlsx) file');
      return;
    }

    // Validate file type
    if (!bulkUploadFile.name.endsWith('.xlsx')) {
      console.log('Invalid file type:', bulkUploadFile.name);
      toastService.error('Only .xlsx files are supported');
      return;
    }

    // Validate file size (10MB limit)
    if (bulkUploadFile.size > 10 * 1024 * 1024) {
      console.log('File too large:', bulkUploadFile.size);
      toastService.error('File is too large. Please upload a file smaller than 10MB');
      return;
    }

    console.log('Starting upload...');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      console.log('FormData created');

      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN);
      console.log('Token found:', !!token);
      console.log('Token value:', token ? token.substring(0, 20) + '...' : 'null');
      if (!token) {
        toastService.error('Please login to access this feature');
        // Just close modal, don't logout since user is not logged in
        setShowBulkUploadModal(false);
        setBulkUploadFile(null);
        setIsUploading(false);
        return;
      }

      console.log('Making API call to:', getApiUrl(API_CONFIG.ENDPOINTS.BULK_UPLOAD));
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.BULK_UPLOAD), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      console.log('API response received:', response.status, response.statusText);

      if (response.status === 401) {
        toastService.error('Session expired. Please login again.');
        // Just close modal, don't call logout API
        setShowBulkUploadModal(false);
        setBulkUploadFile(null);
        setIsUploading(false);
        return;
      }

      if (response.status === 413) {
        toastService.error('File too large. Please upload a smaller file.');
        return;
      }

      if (!response.ok) {
        if (response.status === 400 || response.status === 422) {
          toastService.error('Invalid Excel format. Please use the template.');
        } else {
          toastService.error('Bulk upload failed. Please try again.');
        }
        return;
      }

      // Handle successful response - download the file
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'bulk-upload-result.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toastService.success('Bulk upload completed. Result file downloaded.');
      
      // Refresh user list
      await loadUsers();
      await loadAdmins();
      
      // Close modal and reset
      setShowBulkUploadModal(false);
      setBulkUploadFile(null);
      
    } catch (error) {
      console.error('Bulk upload error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toastService.error('Upload timeout. Please retry.');
      } else {
        toastService.error('Bulk upload failed. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    console.log('handleFileSelect called');
    console.log('Files:', e.target.files);
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.size, file.type);
      setBulkUploadFile(file);
    } else {
      console.log('No file in event');
    }
  };

  const downloadTemplate = () => {
    // Create a template with headers and example data
    const templateContent = `name,email,username,phone,address,designation,event
John Doe,john.doe@example.com,johndoe,+1234567890,123 Main St,Software Engineer,Annual Fest
Jane Smith,jane.smith@example.com,janesmith,+0987654321,456 Oak Ave,Senior Developer,Annual Fest`;
    
    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-upload-template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toastService.success('Template downloaded successfully');
  };

  const createEmptyOption = () => ({
    id: null,
    option_text: '',
    image_file: null,
    image_preview: '',
    image_url: null
  });

  const createEmptyQuestionForm = () => ({
    question_text: '',
    has_images: false,
    question_image_file: null,
    question_image_preview: '',
    question_image_url: null,
    correct_answer: '',
    question_type: 'radio',
    points: 10,
    time_limit: '',
    designationId: null,
    options: [createEmptyOption()],
    correct_answers: []
  });

  const [questionForm, setQuestionForm] = useState(createEmptyQuestionForm());

  const getImageUrl = (url) => QuizService.getMediaUrl(url);

  const getOptionImagePreview = (option) => {
    if (!option) return '';
    return option.image_preview || getImageUrl(option.image_url);
  };

  const getQuestionImagePreview = () => (
    questionForm.question_image_preview || getImageUrl(questionForm.question_image_url)
  );

  const hasOptionContent = (option) => (
    Boolean((option?.option_text || '').trim()) ||
    Boolean(questionForm.has_images && (option?.image_file || option?.image_url))
  );

  const getOptionLabel = (option, index) => {
    const text = (option?.option_text || '').trim();
    if (text) return text;
    if (questionForm.has_images && (option?.image_file || option?.image_url)) return `Image option ${index + 1}`;
    return `Option ${index + 1}`;
  };

  const getAnswerOptionText = (option) => (
    (option?.option_text || option?.text || '').trim()
  );

  const getAnswerOptionLabel = (option, index) => {
    const text = getAnswerOptionText(option);
    if (text) return text;
    if (option?.image_url) return `Image option ${index + 1}`;
    return `Option ${index + 1}`;
  };

  const getCorrectAnswerItems = (correctAnswer, options = []) => {
    const tokens = String(correctAnswer || '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);

    if (tokens.length === 0) return [];

    return tokens.map((token) => {
      const matchIndex = (options || []).findIndex((option) => (
        String(option?.id) === token ||
        getAnswerOptionText(option).toLowerCase() === token.toLowerCase()
      ));

      if (matchIndex === -1) {
        return {
          key: token,
          label: /^\d+$/.test(token) ? `Missing option #${token}` : token,
          imageUrl: ''
        };
      }

      const option = options[matchIndex];
      return {
        key: option?.id || token,
        label: getAnswerOptionLabel(option, matchIndex),
        imageUrl: option?.image_url || ''
      };
    });
  };

  const renderCorrectAnswerDisplay = (correctAnswer, options = [], compact = false) => {
    const items = getCorrectAnswerItems(correctAnswer, options);
    if (items.length === 0) return <span className="correct-answer-empty">N/A</span>;

    return (
      <span className={`correct-answer-display ${compact ? 'compact' : ''}`}>
        {items.map((item) => (
          <span key={item.key} className="correct-answer-display-item">
            <span>{item.label}</span>
            {item.imageUrl && (
              <img
                className="correct-answer-display-image"
                src={getImageUrl(item.imageUrl)}
                alt={item.label}
              />
            )}
          </span>
        ))}
      </span>
    );
  };

  const getSelectedOptionSummary = (answer) => {
    const selected = answer?.selected_options || [];
    if (selected.length === 0) return 'No selection';

    return selected.map((selectedOption, selectedIndex) => {
      const optionIndex = (answer?.options || []).findIndex(option => option.id === selectedOption.id);
      const resolvedOption = optionIndex >= 0 ? answer.options[optionIndex] : selectedOption;
      return getAnswerOptionLabel(resolvedOption, optionIndex >= 0 ? optionIndex : selectedIndex);
    }).join(', ');
  };

  const resetQuestionForm = () => {
    if (questionForm.question_image_preview) {
      URL.revokeObjectURL(questionForm.question_image_preview);
    }
    (questionForm.options || []).forEach((option) => {
      if (option.image_preview) URL.revokeObjectURL(option.image_preview);
    });
    if (questionImageInputRef.current) questionImageInputRef.current.value = '';
    Object.values(optionImageInputRefs.current).forEach((input) => {
      if (input) input.value = '';
    });
    setQuestionForm(createEmptyQuestionForm());
  };

  const normalizeTimeLimitInput = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const compact = String(value).trim().toLowerCase().replace(/\s+/g, '');
    if (!compact) return '';
    if (/^\d+$/.test(compact)) return `${parseInt(compact, 10)}s`;

    const colonMatch = compact.match(/^(\d+):(\d{1,2})$/);
    if (colonMatch) {
      return `${parseInt(colonMatch[1], 10)}m${parseInt(colonMatch[2], 10)}s`;
    }

    return compact;
  };

  const isValidTimeLimitString = (value) => {
    const v = normalizeTimeLimitInput(value);
    if (!v) return false;
    if (!/^(\d+m)?(\d+s)?$/.test(v)) return false;

    const minutes = Number((v.match(/(\d+)m/) || [0, 0])[1]);
    const seconds = Number((v.match(/(\d+)s/) || [0, 0])[1]);
    return minutes * 60 + seconds > 0;
  };

  const resetUserForm = () => {
    console.log('Resetting user form...');
    const cleanForm = {
      name: '',
      username: '',
      email: '',
      password: '',
      avatar: '',
      address: '',
      designationId: '',
      eventId: '',
      status: true
    };
    setFormData(cleanForm);
    setEditingUser(null);
    setUserType('user');
  };

  const resetAdminForm = () => {
    console.log('Resetting admin form...');
    const cleanForm = {
      name: '',
      username: '',
      email: '',
      password: '',
      avatar: '',
      address: '',
      designationId: '',
      eventId: '',
      status: true
    };
    setFormData(cleanForm);
    setEditingUser(null);
    setUserType('admin');
  };

  // Load users, designations, events, and quizzes from API
  useEffect(() => {
    loadUsers();
    loadAdmins();
    loadDesignations();
    loadEvents();
    loadQuizzes();
    loadDropdownOptions();
    loadQuizEvents();
    loadQuizzesWithQuestions();
    loadCompilerLanguages();
    loadLoginBanner();
    loadQuizInstructions();
    loadSecuritySettings();
    loadSessionPolicy();
  }, []);

  // Load dropdown options for user forms
  const loadDropdownOptions = async () => {
    const designationsResult = await UserService.getDesignations();
    if (designationsResult.success) {
      setAvailableDesignations(designationsResult.data);
    }
    
    const eventsResult = await UserService.getEvents();
    if (eventsResult.success) {
      setAvailableEvents(eventsResult.data);
    }
  };

  // Load compiler languages
  const loadCompilerLanguages = async () => {
    try {
      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN) || localStorage.getItem('access_token');
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.COMPILER_LANGUAGES), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.status) {
        setAvailableLanguages(result.data);
      }
    } catch (error) {
      console.error('Error loading compiler languages:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCreateDropdown && !event.target.closest('.create-dropdown')) {
        setShowCreateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateDropdown]);

  // Load events for quiz dropdown
  const loadQuizEvents = async () => {
    const eventsResult = await QuizService.getEvents();
    if (eventsResult.success) {
      setAvailableQuizEvents(eventsResult.data);
    }
  };

  const getDisplayList = () => {
    switch (userListType) {
      case 'users':
        return users;
      case 'admins':
        return admins;
      case 'all':
      default:
        return [...users, ...admins];
    }
  };

  const loadUsers = async (page = userCurrentPage, limit = userItemsPerPage) => {
    const result = await UserService.getUsers(page, limit);
    if (result.success) {
      setUsers(result.data);
      // Update pagination info if pagination metadata is provided
      if (result.pagination) {
        setUserTotalPages(result.pagination.totalPages || 1);
        setUserTotalCount(result.pagination.total || 0);
      } else {
        // If no pagination info, calculate based on current data
        setUserTotalPages(result.data.length === limit ? page + 1 : page);
        setUserTotalCount(result.data.length);
      }
    } else {
      toastService.error(result.message);
    }
  };

  const loadAdmins = async (page = userCurrentPage, limit = userItemsPerPage) => {
    const result = await UserService.getAdmins(page, limit);
    if (result.success) {
      setAdmins(result.data);
      // Update pagination info if pagination metadata is provided
      if (result.pagination) {
        setUserTotalPages(result.pagination.totalPages || 1);
        setUserTotalCount(result.pagination.total || 0);
      } else {
        // If no pagination info, calculate based on current data
        setUserTotalPages(result.data.length === limit ? page + 1 : page);
        setUserTotalCount(result.data.length);
      }
    } else {
      toastService.error(result.message);
    }
  };

  const loadDesignations = async () => {
    const result = await UserService.getDesignations();
    if (result.success) {
      setDesignations(result.data);
      setTotalDesignations(result.data.length);
    } else {
      toastService.error(result.message);
    }
  };

  const loadEvents = async () => {
    const result = await UserService.getEvents();
    if (result.success) {
      setEvents(result.data);
      setTotalEvents(result.data.length);
    } else {
      toastService.error(result.message);
    }
  };

  // Quiz Builder functions
  const loadQuizzes = async (page = currentPage, limit = itemsPerPage) => {
    // Reset fetch ref to allow refresh when called with specific parameters
    didFetchQuizzes.current = false;
    
    setLoading(true);
    const result = await QuizService.getQuizzes(page, limit);
    if (result.success) {
      setQuizzes(result.data);
      setTotalQuizzes(result.data.length);
      // Calculate total pages if we have pagination info
      // For now, assume we have data if result.data.length === limit
      if (result.data.length === limit) {
        setTotalPages(page + 1); // There might be more pages
      } else {
        setTotalPages(page); // This is the last page
      }
    } else {
      toastService.error(result.message || 'Failed to load quizzes');
    }
    setLoading(false);
  };

  const loadQuizzesWithQuestions = async (page = 1, limit = 20) => {
    // Prevent duplicate calls in React StrictMode
    if (didFetchQuizzesWithQuestions.current) {
      return;
    }
    didFetchQuizzesWithQuestions.current = true;
    
    setLoadingQuizzesWithQuestions(true);
    const result = await QuizService.getQuizzesWithQuestions(page, limit);
    if (result.success) {
      setQuizzesWithQuestions(result.data);
    } else {
      // Handle specific error cases
      if (result.message?.includes('Session expired') || result.message?.includes('Unauthorized')) {
        toastService.error('Session expired');
        return;
      } else if (result.message?.includes('Server error')) {
        toastService.error('Failed to load quizzes');
      } else {
        toastService.error(result.message || 'Network error. Please try again.');
      }
    }
    setLoadingQuizzesWithQuestions(false);
  };

  const toggleQuizExpansion = (quizId) => {
    setExpandedQuizzes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(quizId)) {
        newSet.delete(quizId);
      } else {
        newSet.add(quizId);
      }
      return newSet;
    });
  };

  // Helper functions for formatting
  const formatDateTime = (dateString) => {
    return formatUtcDateTime(dateString);
  };

  const formatTotalTime = (totalTime) => {
    return formatDuration(totalTime);
  };

  const formatTimeLimit = (timeLimit) => {
    if (timeLimit === null || timeLimit === undefined || timeLimit === '') return 'No limit';
    return formatDuration(timeLimit);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const showFullText = (text) => {
    return text || 'No text';
  };

  const buildQuestionPayload = () => {
    console.log("questionform:", questionForm)
    console.log('🔍 AdminDashboard.buildQuestionPayload - questionForm.designationId:', questionForm.designationId);
    console.log('🔍 AdminDashboard.buildQuestionPayload - typeof questionForm.designationId:', typeof questionForm.designationId);
    
    const question_text = (questionForm.question_text || '').trim();
    const question_type = questionForm.question_type;
    const points = parseInt(questionForm.points);
    const designationId = Number(questionForm.designationId) || null;
    const rawTimeLimit = questionForm.time_limit ?? '';
    const time_limit = normalizeTimeLimitInput(rawTimeLimit);
    
    console.log('🔍 AdminDashboard.buildQuestionPayload - final designationId:', designationId);

    if (!question_text) return { ok: false, message: 'Question text is required.' };
    if (!question_type) return { ok: false, message: 'Question type is required.' };
    if (!points || points <= 0) return { ok: false, message: 'Points must be a positive number' };
    if (!time_limit) return { ok: false, message: 'Time limit is required.' };
    if (!isValidTimeLimitString(time_limit)) {
      return { ok: false, message: 'Time limit should look like 20s, 1m, or 2m30s.' };
    }

    const basePayload = {
      question_text,
      question_type,
      designationId,
      points,
      time_limit,
      ...(!questionForm.has_images ? { question_image_url: null } : {})
    };
    
    console.log('🔍 AdminDashboard.buildQuestionPayload - basePayload:', JSON.stringify(basePayload, null, 2));
    console.log('🔍 AdminDashboard.buildQuestionPayload - basePayload.designationId:', basePayload.designationId);

    if (question_type === 'radio' || question_type === 'checkbox') {
      const optionInputs = (questionForm.options || [])
        .map((option, index) => ({
          ...option,
          index,
          option_text: (option?.option_text || '').trim()
        }))
        .filter(hasOptionContent);

      if (optionInputs.length < 2) return { ok: false, message: 'Radio/Checkbox questions need at least 2 options' };

      const hasMedia = Boolean(questionForm.has_images) && (
        Boolean(questionForm.question_image_file || questionForm.question_image_url) ||
        optionInputs.some(option => option.image_file || option.image_url)
      );

      if (question_type === 'radio') {
        const correctIndexes = questionForm.correct_answer !== '' ? [Number(questionForm.correct_answer)] : [];
        if (correctIndexes.length < 1 || Number.isNaN(correctIndexes[0])) {
          return { ok: false, message: 'Correct answer is required.' };
        }
        if (!optionInputs.some(option => option.index === correctIndexes[0])) {
          return { ok: false, message: 'Correct answer must be one of the options' };
        }

        if (hasMedia) {
          return {
            ok: true,
            payload: basePayload,
            optionInputs,
            correctIndexes,
            requiresOptionSync: true
          };
        }

        const trimmedOptions = optionInputs.map(o => ({ option_text: o.option_text }));
        const correctOption = optionInputs.find(option => option.index === correctIndexes[0]);
        return {
          ok: true,
          payload: {
            ...basePayload,
            correct_answer: correctOption.option_text,
            options: trimmedOptions
          },
          optionInputs,
          correctIndexes,
          requiresOptionSync: false
        };
      }

      const selected = Array.isArray(questionForm.correct_answers) ? questionForm.correct_answers : [];
      const correctIndexes = selected.map(Number).filter(index => !Number.isNaN(index));
      if (correctIndexes.length < 1) return { ok: false, message: 'At least 1 correct answer must be selected' };
      if (correctIndexes.some(index => !optionInputs.some(option => option.index === index))) {
        return { ok: false, message: 'Correct answer must be one of the options' };
      }

      if (hasMedia) {
        return {
          ok: true,
          payload: basePayload,
          optionInputs,
          correctIndexes,
          requiresOptionSync: true
        };
      }

      const trimmedOptions = optionInputs.map(o => ({ option_text: o.option_text }));
      const correctTexts = correctIndexes
        .map(index => optionInputs.find(option => option.index === index)?.option_text)
        .filter(Boolean);

      return {
        ok: true,
        payload: {
          ...basePayload,
          correct_answer: correctTexts.join(','),
          options: trimmedOptions
        },
        optionInputs,
        correctIndexes,
        requiresOptionSync: false
      };
    }

    if (question_type === 'text' || question_type === 'code') {
      const correct = (questionForm.correct_answer || '').toString();
      if (!correct.trim()) return { ok: false, message: 'Correct answer is required.' };
      return {
        ok: true,
        payload: {
          ...basePayload,
          correct_answer: correct
        }
      };
    }

    return { ok: false, message: 'Unsupported question type.' };
  };

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const readFormValue = (name) => {
      const value = formData.get(name);
      return typeof value === 'string' ? value : '';
    };
    const formEventId = Number(readFormValue('eventId'));
    const formTotalTime = readFormValue('total_time');
    const effectiveQuizForm = {
      ...quizForm,
      title: quizForm.title || readFormValue('title'),
      description: quizForm.description || readFormValue('description'),
      start_date: quizForm.start_date || readFormValue('start_date'),
      end_date: quizForm.end_date || readFormValue('end_date'),
      eventId: quizForm.eventId || (Number.isFinite(formEventId) ? formEventId : null),
      total_time: quizForm.total_time !== undefined && quizForm.total_time !== null && quizForm.total_time !== ''
        ? quizForm.total_time
        : formTotalTime,
    };

    // Validation
    const errors = [];
    if (!effectiveQuizForm.title.trim()) errors.push('Quiz title is required');
    if (!effectiveQuizForm.start_date) errors.push('Start date is required');
    if (!effectiveQuizForm.end_date) errors.push('End date is required');
    if (!effectiveQuizForm.eventId || effectiveQuizForm.eventId === 0) errors.push('Please select an event');
    const totalTimeMinutes = Number(effectiveQuizForm.total_time);
    if (!totalTimeMinutes || totalTimeMinutes <= 0) errors.push('Total time must be a positive number');
    if (totalTimeMinutes > 600) errors.push('Total time cannot exceed 600 minutes');

    // Date validation
    const isoStartDate = localDateTimeInputToUtcIso(effectiveQuizForm.start_date);
    const isoEndDate = localDateTimeInputToUtcIso(effectiveQuizForm.end_date);
    const startDate = new Date(isoStartDate);
    const endDate = new Date(isoEndDate);
    if (!isoStartDate || !isoEndDate) errors.push('Start and end dates must be valid');
    if (startDate >= endDate) errors.push('End date must be after start date');

    if (errors.length) {
      const toastMsg = `Please fix the following:\n${errors.map(e => `- ${e}`).join('\n')}`;
      toastService.error(toastMsg);
      setLoading(false);
      return;
    }

    const quizData = {
      title: effectiveQuizForm.title.trim(),
      description: effectiveQuizForm.description.trim(),
      start_date: isoStartDate,
      end_date: isoEndDate,
      total_time: minutesToSeconds(totalTimeMinutes),
      eventId: effectiveQuizForm.eventId
    };

    let result;
    if (quizModalMode === 'edit') {
      // Update existing quiz
      result = await QuizService.updateQuiz(selectedQuiz.id, quizData);
      if (result.success) {
        toastService.success('Quiz updated successfully');
      }
    } else {
      // Create new quiz
      result = await QuizService.createQuiz(quizData);
      if (result.success) {
        toastService.success('Quiz created successfully');
      }
    }

    if (result.success) {
      // Reset form and close modal
      setQuizForm({ 
        title: '', 
        description: '', 
        start_date: '',
        end_date: '',
        total_time: 60,
        eventId: null
      });
      setShowQuizForm(false);
      setQuizModalMode('create');
      setSelectedQuiz(null);
      
      // Refresh quiz list
      await loadQuizzes(currentPage, itemsPerPage);
    } else {
      // Handle specific error cases
      if (result.message?.includes('Session expired') || result.message?.includes('Unauthorized')) {
        // 401 is already handled by apiClient, but add extra safety
        toastService.error('Session expired');
        return;
      } else if (result.message?.includes('validation') || result.message?.includes('required')) {
        // 400 Validation errors
        toastService.error('Please check the form fields');
      } else {
        // 500 or other errors
        toastService.error(`Failed to ${quizModalMode === 'edit' ? 'update' : 'create'} quiz`);
      }
    }
    setLoading(false);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const trimmedName = eventForm.name.trim();
    const trimmedDescription = eventForm.description.trim();
    
    if (!trimmedName) {
      toastService.error('Event name is required.');
      return;
    }
    
    setLoading(true);

    const result = await UserService.createEvent({ 
      name: trimmedName,
      description: trimmedDescription 
    });
    
    if (result.success) {
      toastService.success('Event created successfully!');
      setEventForm({ name: '', description: '' });
      setShowEventForm(false);
      await loadEvents();
      await loadQuizEvents(); // Also update quiz events dropdown
      await loadDropdownOptions(); // Update user form dropdowns
    } else {
      toastService.error(result.message);
    }
    setLoading(false);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    
    if (!editingEvent) {
      toastService.error('No event selected for editing');
      return;
    }

    const trimmedName = eventForm.name.trim();
    const trimmedDescription = eventForm.description.trim();
    
    if (!trimmedName) {
      toastService.error('Event name is required.');
      return;
    }
    
    setLoading(true);

    const result = await UserService.updateEvent(editingEvent.id, { 
      name: trimmedName,
      description: trimmedDescription 
    });
    
    if (result.success) {
      toastService.success('Event updated successfully!');
      resetEventForm();
      await loadEvents();
      await loadQuizEvents(); // Also update quiz events dropdown
      await loadDropdownOptions(); // Update user form dropdowns
    } else {
      toastService.error(result.message);
    }
    setLoading(false);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name || '',
      description: event.description || ''
    });
    setShowEventForm(true);
  };

  // Helper function to show delete confirmation
  const showDeleteConfirm = (entityType, entityLabel, impactWarnings, onConfirm) => {
    setDeleteConfirm({
      isOpen: true,
      entityType,
      entityLabel,
      impactWarnings,
      onConfirm,
      isDeleting: false
    });
  };

  const handleDeleteEvent = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    showDeleteConfirm(
      'Event',
      event.name,
      [
        'Deleting this event may also remove associated quizzes, users assigned to this event, and related Answer Sheets / results.'
      ],
      () => performDeleteEvent(eventId)
    );
  };

  const performDeleteEvent = async (eventId) => {
    setDeleteConfirm(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const result = await UserService.deleteEvent(eventId);
      if (result.success) {
        toastService.success('Event deleted successfully');
        await loadEvents();
      } else {
        toastService.error(result.message || 'Failed to delete event');
      }
    } catch (error) {
      console.error('Delete event error:', error);
      toastService.error('Network error while deleting event');
    } finally {
      setDeleteConfirm(prev => ({ ...prev, isOpen: false, isDeleting: false }));
    }
  };

  const resetEventForm = () => {
    setEventForm({ name: '', description: '' });
    setEditingEvent(null);
    setShowEventForm(false);
  };

  const resetDesignationForm = () => {
    setDesignationForm({ name: '', description: '' });
    setEditingDesignation(null);
    setShowDesignationForm(false);
  };

  const handleQuizModalClose = () => {
  // Check if form has unsaved changes
  const hasUnsavedChanges = quizForm.title || quizForm.description || quizForm.start_date || 
                           quizForm.end_date || quizForm.eventId || quizForm.total_time !== 60;
  
  if (hasUnsavedChanges) {
    if (window.confirm('Discard changes?')) {
      setShowQuizForm(false);
      resetQuizForm();
    }
  } else {
    setShowQuizForm(false);
    resetQuizForm();
  }
};

const resetQuizForm = () => {
    setQuizForm({
      title: '', 
      description: '', 
      start_date: '', 
      end_date: '', 
      total_time: 60,
      eventId: null
    });
    setQuizModalMode('create');
    setSelectedQuiz(null);
    setShowQuizForm(false);
  };

  const handleCreateDesignation = async (e) => {
    e.preventDefault();
    
    if (!designationForm.name.trim()) {
      toastService.error('Please enter a designation name');
      return;
    }

    const designationData = {
      name: designationForm.name.trim(),
      description: designationForm.description.trim()
    };

    const result = await UserService.createDesignation(designationData);
    
    if (result.success) {
      await loadDesignations();
      await loadDropdownOptions(); // Update user form dropdowns
      resetDesignationForm();
      toastService.success('Designation created successfully');
    } else {
      toastService.error(result.message);
    }
  };

  const handleUpdateDesignation = async (e) => {
    e.preventDefault();
    
    if (!editingDesignation) {
      toastService.error('No designation selected for editing');
      return;
    }

    if (!designationForm.name.trim()) {
      toastService.error('Please enter a designation name');
      return;
    }

    const designationData = {
      name: designationForm.name.trim(),
      description: designationForm.description.trim()
    };

    const result = await UserService.updateDesignation(editingDesignation.id, designationData);
    
    if (result.success) {
      await loadDesignations();
      await loadDropdownOptions(); // Update user form dropdowns
      resetDesignationForm();
      toastService.success('Designation updated successfully');
    } else {
      toastService.error(result.message);
    }
  };

  const handleEditDesignation = (designation) => {
    setEditingDesignation(designation);
    setDesignationForm({
      name: designation.name || '',
      description: designation.description || ''
    });
    setShowDesignationForm(true);
  };

  const handleDeleteDesignation = (designationId) => {
    const designation = designations.find(d => d.id === designationId);
    if (!designation) return;
    
    showDeleteConfirm(
      'Designation',
      designation.name,
      [
        'Deleting this designation may affect users/quizzes linked to it. Ensure no active dependencies exist.'
      ],
      () => performDeleteDesignation(designationId)
    );
  };

  const performDeleteDesignation = async (designationId) => {
    setDeleteConfirm(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const result = await UserService.deleteDesignation(designationId);
      if (result.success) {
        toastService.success('Designation deleted successfully');
        await loadDesignations();
        await loadDropdownOptions();
      } else {
        toastService.error(result.message || 'Failed to delete designation');
      }
    } catch (error) {
      console.error('Delete designation error:', error);
      toastService.error('Network error while deleting designation');
    } finally {
      setDeleteConfirm(prev => ({ ...prev, isOpen: false, isDeleting: false }));
    }
  };

  const handleAddDesignation = () => {
    setShowDesignationForm(true);
    setEditingDesignation(null);
    setDesignationForm({ name: '', description: '' });
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    console.log('Admin form submitted! userType: admin');
    console.log('Admin form data:', formData);
    console.log('Editing user:', editingUser);

    const normalizedUsername = (formData.username || '').includes('@')
      ? (formData.username || '').split('@')[0]
      : (formData.username || '');
    if (normalizedUsername !== formData.username) {
      setFormData((prev) => ({ ...prev, username: normalizedUsername }));
      toastService.info('Username was auto-updated from email to a valid username.');
    }
    
    // Ensure all required fields are filled
    const isEditMode = !!editingUser;
    console.log('🔍 Validation Mode:', isEditMode ? 'EDIT MODE' : 'CREATE MODE');
    console.log('🔍 Form Data:', formData);
    console.log('🔍 Editing User:', editingUser);
    
    // In edit mode, make all fields completely optional
    if (isEditMode) {
      // No required fields in edit mode - user can update any field they want
      console.log('✅ Edit Mode: All fields are optional - no validation required');
      console.log('🔍 Fields that will be sent if have values:');
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value) {
          console.log(`  - ${key}: ${value}`);
        }
      });
    } else {
      // In create mode, require all fields (except designation/event for admins)
      console.log('⚠️ Create Mode: Validating all required fields');
      const requiredFields = [
        { field: 'name', label: 'Name' },
        { field: 'username', label: 'Username' },
        { field: 'email', label: 'Email' },
        { field: 'password', label: 'Password' }
        // Note: Admins don't use designationId/eventId, they use roleId: 0
      ];
      
      console.log('🔍 Required Fields Check:', requiredFields.map(f => ({ field: f.field, value: formData[f.field], valid: !(!formData[f.field] || (f.checkZero && (formData[f.field] === 0 || formData[f.field] === '0'))) })));
      
      const missingFields = requiredFields.filter(({ field, checkZero }) => {
        const value = formData[field];
        const isValid = !(!value || (checkZero && (value === 0 || value === '0')));
        return !isValid;
      });
      
      console.log('🔍 Missing Fields:', missingFields);
      
      if (missingFields.length > 0) {
        const fieldLabels = missingFields.map(f => f.label).join(', ');
        console.log('❌ Validation Failed - Missing:', fieldLabels);
        toastService.error(`Please fill in all required fields: ${fieldLabels}.`);
        return;
      }
      
      console.log('✅ Create Mode Validation Passed');
    }
    
    console.log('Admin form data after validation:', formData);
    
    // Create admin with roleId - create clean object to avoid corruption
    const adminData = {
      name: formData.name,
      username: normalizedUsername,
      phone: formData.phone || '',
      email: formData.email,
      avatar: formData.avatar || '',
      address: formData.address || 'string',
      roleId: 0 // Admin API uses roleId only
    };
    
    // Only include password if it's provided and meets minimum requirements
    if (formData.password && formData.password.length >= 8) {
      // Check if password meets backend requirements
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumbers = /\d/.test(formData.password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
      
      if (hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar) {
        adminData.password = formData.password;
        console.log('✅ Password meets requirements, will be updated');
      } else {
        console.log('❌ Password does not meet requirements, skipping password update');
        toastService.warning('Password does not meet requirements. Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character, and be at least 8 characters long.');
        return;
      }
    } else if (formData.password) {
      console.log('❌ Password too short, skipping password update');
      toastService.warning('Password must be at least 8 characters long to update.');
      return;
    } else {
      console.log('ℹ️ No password provided, keeping existing password');
    }
    
    console.log('Clean admin data:', adminData);
    
    let result;
    if (editingUser) {
      // Update existing admin
      console.log('Updating existing admin with ID:', editingUser.id);
      result = await UserService.updateAdmin(editingUser.id, adminData);
    } else {
      // Create new admin
      console.log('Creating new admin');
      result = await UserService.createAdmin(adminData);
    }
    
    console.log('Admin API result:', result);
    
    if (result.success) {
      console.log('✅ Admin success - refreshing lists...');
      // Refresh both lists
      await loadUsers();
      await loadAdmins();

      // Close modal + reset
      setShowAdminForm(false);
      resetAdminForm();
      setEditingUser(null);
      
      // Show appropriate success message
      if (editingUser) {
        toastService.success('Admin updated successfully!');
      } else {
        toastService.success('Admin created successfully!');
      }
    } else {
      console.log('❌ Admin failed - showing error:', result.message);
      toastService.error(result.message);
    }
  };

  const handleCreateRegularUser = async (e) => {
    e.preventDefault();
    console.log('User form submitted! userType: user');
    console.log('User form data:', formData);
    console.log('Editing user:', editingUser);

    const normalizedUsername = (formData.username || '').includes('@')
      ? (formData.username || '').split('@')[0]
      : (formData.username || '');
    if (normalizedUsername !== formData.username) {
      setFormData((prev) => ({ ...prev, username: normalizedUsername }));
      toastService.info('Username was auto-updated from email to a valid username.');
    }
    
    // Ensure all required fields are filled
    const isEditMode = !!editingUser;
    console.log('🔍 Validation Mode:', isEditMode ? 'EDIT MODE' : 'CREATE MODE');
    console.log('🔍 Form Data:', formData);
    console.log('🔍 Editing User:', editingUser);
    
    // In edit mode, make all fields completely optional
    if (isEditMode) {
      // No required fields in edit mode - user can update any field they want
      console.log('✅ Edit Mode: All fields are optional - no validation required');
      console.log('🔍 Fields that will be sent if have values:');
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value) {
          console.log(`  - ${key}: ${value}`);
        }
      });
    } else {
      // In create mode, require all fields
      console.log('⚠️ Create Mode: Validating all required fields');
      const requiredFields = [
        { field: 'name', label: 'Name' },
        { field: 'username', label: 'Username' },
        { field: 'email', label: 'Email' },
        { field: 'password', label: 'Password' },
        { field: 'designationId', label: 'Designation', checkZero: true },
        { field: 'eventId', label: 'Event', checkZero: true }
      ];
      
      console.log('🔍 Required Fields Check:', requiredFields.map(f => ({ field: f.field, value: formData[f.field], valid: !(!formData[f.field] || (f.checkZero && (formData[f.field] === 0 || formData[f.field] === '0'))) })));
      
      const missingFields = requiredFields.filter(({ field, checkZero }) => {
        const value = formData[field];
        const isValid = !(!value || (checkZero && (value === 0 || value === '0')));
        return !isValid;
      });
      
      console.log('🔍 Missing Fields:', missingFields);
      
      if (missingFields.length > 0) {
        const fieldLabels = missingFields.map(f => f.label).join(', ');
        console.log('❌ Validation Failed - Missing:', fieldLabels);
        toastService.error(`Please fill in all required fields: ${fieldLabels}.`);
        return;
      }
      
      console.log('✅ Create Mode Validation Passed');
    }
    
    console.log('User form data after validation:', formData);
    
    // Create user with designationId and eventId - create clean object to avoid corruption
    const userData = {
      name: formData.name,
      username: normalizedUsername,
      phone: formData.phone,
      email: formData.email,
      avatar: formData.avatar,
      address: formData.address,
      designationId: Number(formData.designationId),
      eventId: Number(formData.eventId)
    };
    
    // Only include password if it's provided and meets minimum requirements
    if (formData.password && formData.password.length >= 8) {
      // Check if password meets backend requirements
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumbers = /\d/.test(formData.password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
      
      if (hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar) {
        userData.password = formData.password;
        console.log('✅ Password meets requirements, will be updated');
      } else {
        console.log('❌ Password does not meet requirements, skipping password update');
        toastService.warning('Password does not meet requirements. Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character, and be at least 8 characters long.');
        return;
      }
    } else if (formData.password) {
      console.log('❌ Password too short, skipping password update');
      toastService.warning('Password must be at least 8 characters long to update.');
      return;
    } else {
      console.log('ℹ️ No password provided, keeping existing password');
    }
    
    console.log('Clean user data:', userData);
    
    let result;
    if (editingUser) {
      // Update existing user
      console.log('Updating existing user with ID:', editingUser.id);
      result = await UserService.updateUser(editingUser.id, userData);
    } else {
      // Create new user
      console.log('Creating new user');
      result = await UserService.createUser(userData);
    }
    
    console.log('User API result:', result);
    
    if (result.success) {
      console.log('✅ User success - refreshing lists...');
      // Refresh both lists
      await loadUsers();
      await loadAdmins();

      // Close modal + reset
      setShowRegularUserForm(false);
      resetUserForm();
      setEditingUser(null);
      
      // Show appropriate success message
      if (editingUser) {
        toastService.success('User updated successfully!');
      } else {
        toastService.success('User created successfully!');
      }
    } else {
      console.log('❌ User failed - showing error:', result.message);
      toastService.error(result.message);
    }
  };

  const handleLogout = async () => {
    await AuthService.logout();
    onLogout();
  };

  const handleDeleteUser = (userId, isAdmin = false) => {
    const userList = isAdmin ? admins : users;
    const user = userList.find(u => u.id === userId);
    if (!user) return;
    
    showDeleteConfirm(
      isAdmin ? 'Admin' : 'User',
      `${user.name} (${user.email})`,
      [
        'Deleting this user will remove user-specific Answer Sheets (their submissions) and may affect results.'
      ],
      () => performDeleteUser(userId, isAdmin)
    );
  };

  const performDeleteUser = async (userId, isAdmin = false) => {
    setDeleteConfirm(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const deleteMethod = isAdmin ? UserService.deleteAdmin : UserService.deleteUser;
      const result = await deleteMethod(userId);
      
      if (result.success) {
        toastService.success(`${isAdmin ? 'Admin' : 'User'} deleted successfully`);
        await loadUsers();
        await loadAdmins();
      } else {
        toastService.error(result.message || `Failed to delete ${isAdmin ? 'admin' : 'user'}`);
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toastService.error('Network error while deleting user');
    } finally {
      setDeleteConfirm(prev => ({ ...prev, isOpen: false, isDeleting: false }));
    }
  };

  const handleEditUser = (user, isAdmin = false) => {
    console.log('=== EDIT USER CALLED ===', user, isAdmin);
    
    if (availableDesignations.length === 0 || availableEvents.length === 0) {
      loadDropdownOptions();
    }
    
    setEditingUser(user);
    // Check if this is an admin user (has roleId) or regular user (has designationId)
    const isAdminUser = (user.roleId !== undefined && user.roleId !== null) || 
                     (user.designation === undefined && user.event === undefined && user.designationId === undefined && user.eventId === undefined);
    console.log('Detected user type:', isAdminUser ? 'admin' : 'user');
    console.log('🔍 User data for type detection:', { roleId: user.roleId, designation: user.designation, event: user.event });
    setUserType(isAdminUser ? 'admin' : 'user');
    
    // Map the designation and event IDs properly
    const designationId = user.designationId || user.designation?.id || null;
    const eventId = user.eventId || user.event?.id || null;
    
    console.log('🔍 Mapped designationId:', designationId);
    console.log('🔍 Mapped eventId:', eventId);
    console.log('🔍 Available designations:', availableDesignations);
    console.log('🔍 Available events:', availableEvents);
    
    setFormData({
      name: user.name || '',
      username: user.username || '',
      email: user.email || '',
      password: '', // Don't populate password in edit mode
      avatar: user.avatar || '',
      address: user.address || '',
      designationId: designationId || 0,
      eventId: eventId || 0,
      status: user.status !== undefined ? user.status : true
    });
    
    // Show the correct form based on user type
    if (isAdminUser) {
      setShowAdminForm(true);
      setShowRegularUserForm(false);
    } else {
      setShowRegularUserForm(true);
      setShowAdminForm(false);
    }
    console.log('=== SHOW USER FORM CALLED ===');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          const newData = {
            id: quizData.length + 1,
            title: jsonData.title || file.name.replace('.json', ''),
            questions: jsonData.questions?.length || 0,
            active: true,
            uploadedBy: 'admin',
            uploadDate: new Date().toISOString().split('T')[0]
          };
          setQuizData([...quizData, newData]);
          alert('Quiz data uploaded successfully!');
        } catch (error) {
          alert('Invalid JSON file. Please check the format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const toggleQuizStatus = (quizId) => {
    setQuizData(quizData.map(quiz => 
      quiz.id === quizId ? { ...quiz, active: !quiz.active } : quiz
    ));
  };

  const handleDeleteQuiz = (quizId) => {
    const quiz = quizzes.find(q => q.id === quizId);
    if (!quiz) return;
    
    showDeleteConfirm(
      'Quiz',
      quiz.title,
      [
        'Deleting a quiz may remove related questions and answer sheets.'
      ],
      () => performDeleteQuiz(quizId)
    );
  };

  const handleViewQuiz = (quiz) => {
    // For now, navigate to questions view or show quiz details
    // This could be expanded to a separate details page
    setSelectedQuiz(quiz);
    loadQuizQuestions(quiz.id);
  };

  const performDeleteQuiz = async (quizId) => {
    setDeleteConfirm(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const result = await QuizService.deleteQuiz(quizId);
      if (result.success) {
        toastService.success('Quiz deleted successfully');
        await loadQuizzes(currentPage, itemsPerPage);
      } else {
        toastService.error(result.message || 'Failed to delete quiz');
      }
    } catch (error) {
      console.error('Delete quiz error:', error);
      toastService.error('Network error while deleting quiz');
    } finally {
      setDeleteConfirm(prev => ({ ...prev, isOpen: false, isDeleting: false }));
    }
  };

  const handleEditQuiz = (quiz) => {
    // Set modal mode to edit and store selected quiz
    setQuizModalMode('edit');
    setSelectedQuiz(quiz);
    
    // Prefill form with quiz data
    setQuizForm({
      title: quiz.title || '',
      description: quiz.description || '',
      start_date: utcIsoToLocalDateTimeInput(quiz.start_date),
      end_date: utcIsoToLocalDateTimeInput(quiz.end_date),
      total_time: secondsToMinutes(quiz.total_time),
      eventId: quiz.event?.id || null
    });
    
    // Open quiz modal
    setShowQuizForm(true);
  };

  const loadQuizQuestions = async (quizId) => {
    setLoading(true);
    const result = await QuizService.getQuizQuestions(quizId);
    if (result.success) {
      setQuizQuestions(result.data);
      const numericId = Number(quizId);
      setSelectedQuiz(quizzes.find(q => Number(q.id) === numericId));
      setShowQuestionsView(true);
    } else {
      toastService.error(result.message);
    }
    setLoading(false);
  };

  const handleQuizSelect = (quiz) => {
    setSelectedQuiz(quiz);
    loadQuizQuestions(quiz.id);
  };

  const handleBackToQuizzes = () => {
    setShowQuestionsView(false);
    setSelectedQuiz(null);
    setQuizQuestions([]);
  };

  const syncQuestionMediaAndCorrectAnswers = async (quizId, questionId, built) => {
    if (questionForm.has_images && questionForm.question_image_file) {
      const imageResult = await QuizService.uploadQuestionImage(
        quizId,
        questionId,
        questionForm.question_image_file
      );
      if (!imageResult.success) {
        throw new Error(imageResult.message || 'Failed to upload question image');
      }
    }

    const optionIdByIndex = new Map();

    for (const option of built.optionInputs || []) {
      if (option.id && !option.image_file) {
        optionIdByIndex.set(option.index, option.id);
        continue;
      }

      const optionText = (option.option_text || '').trim();
      const result = option.image_file
        ? await QuizService.uploadQuestionOptionImage(quizId, questionId, option.image_file, optionText)
        : await QuizService.createQuestionOption(quizId, questionId, optionText);

      if (!result.success) {
        throw new Error(result.message || `Failed to save option ${option.index + 1}`);
      }

      const savedOption = result.data;
      if (!savedOption?.id) {
        throw new Error(`Saved option ${option.index + 1} did not return an ID`);
      }

      optionIdByIndex.set(option.index, savedOption.id);
    }

    if (built.correctIndexes?.length) {
      const correctIds = built.correctIndexes.map(index => optionIdByIndex.get(index)).filter(Boolean);
      if (correctIds.length !== built.correctIndexes.length) {
        throw new Error('Could not resolve all correct option IDs');
      }

      const updateResult = await QuizService.updateQuestion(quizId, questionId, {
        correct_answer: correctIds.join(',')
      });

      if (!updateResult.success) {
        throw new Error(updateResult.message || 'Failed to save correct answer');
      }
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!selectedQuiz) {
      toastService.error('Please select a quiz first');
      return;
    }

    console.log('Submitting question for quizId:', selectedQuiz?.id);

    const built = buildQuestionPayload();
    if (!built.ok) {
      console.warn('buildQuestionPayload failed:', built?.message, {
        questionForm,
        selectedQuizId: selectedQuiz?.id
      });
      toastService.error(built.message);
      return;
    }

    setLoading(true);

    try {
      console.log('Creating question for quizId:', selectedQuiz?.id, built.payload);

      const result = await QuizService.addQuestion(selectedQuiz.id, built.payload);
      if (result.success) {
        const createdQuestion = result.data;
        if (built.requiresOptionSync || (questionForm.has_images && questionForm.question_image_file)) {
          await syncQuestionMediaAndCorrectAnswers(selectedQuiz.id, createdQuestion.id, built);
        }

        toastService.success('Question added successfully!');
        resetQuestionForm();
        setShowQuestionForm(false);
        await loadQuizQuestions(selectedQuiz.id);
      } else {
        toastService.error(result.message);
      }
    } catch (error) {
      console.error('Create question error:', error?.response?.data || error);
      toastService.error(error?.response?.data?.message || error?.message || 'Failed to create question');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!selectedQuiz || !editingQuestion) {
      toastService.error('Quiz or question not selected');
      return;
    }

    console.log('Updating question for quizId:', selectedQuiz?.id, editingQuestion?.id);

    const built = buildQuestionPayload();
    if (!built.ok) {
      toastService.error(built.message);
      return;
    }

    setLoading(true);

    try {
      console.log('Updating question for quizId:', selectedQuiz?.id, editingQuestion?.id, built.payload);

      const result = await QuizService.updateQuestion(selectedQuiz.id, editingQuestion.id, built.payload);
      if (result.success) {
        if (built.requiresOptionSync || (questionForm.has_images && questionForm.question_image_file)) {
          await syncQuestionMediaAndCorrectAnswers(selectedQuiz.id, editingQuestion.id, built);
        }

        toastService.success('Question updated successfully!');
        resetQuestionForm();
        setShowQuestionForm(false);
        setEditingQuestion(null);
        setIsEditMode(false);

        await loadQuizQuestions(selectedQuiz.id);
      } else {
        toastService.error(result.message);
      }
    } catch (error) {
      console.error('Update question error:', error?.response?.data || error);
      toastService.error(error?.response?.data?.message || error?.message || 'Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    console.log('🔍 Edit Question Data:', question);
    console.log('🔍 Designation object:', question.designation);
    console.log('🔍 Available designations:', availableDesignations);
    
    setEditingQuestion(question);
    setIsEditMode(true);
    
    // Ensure designations are loaded for dropdown
    if (availableDesignations.length === 0) {
      loadDropdownOptions();
    }
    
    const designationId = question.designationId || question.designation_id || (question.designation?.id) || null;
    console.log('🔍 Mapped designationId:', designationId);
    
    const mappedOptions = question.options && question.options.length > 0
      ? question.options.map(o => ({
          id: o.id || null,
          option_text: o.option_text || '',
          image_file: null,
          image_preview: '',
          image_url: o.image_url || null
        }))
      : [createEmptyOption()];

    const correctTokens = new Set(
      (question.correct_answer || '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
    );
    const correctIndexes = mappedOptions
      .map((option, index) => (
        correctTokens.has(String(option.id)) || correctTokens.has((option.option_text || '').trim())
          ? String(index)
          : null
      ))
      .filter(value => value !== null);
    const hasExistingImages = Boolean(question.question_image_url) ||
      mappedOptions.some(option => Boolean(option.image_url));

    setQuestionForm({
      question_text: question.question_text || '',
      has_images: hasExistingImages,
      question_image_file: null,
      question_image_preview: '',
      question_image_url: question.question_image_url || null,
      correct_answer: question.correct_answer || '',
      question_type: question.question_type || 'radio',
      points: question.points || 10,
      time_limit: question.time_limit || '',
      designationId: designationId,
      options: mappedOptions,
      correct_answers: correctIndexes
    });

    if (question.question_type === 'radio') {
      setQuestionForm(prev => ({
        ...prev,
        correct_answer: correctIndexes[0] || ''
      }));
    }
    setShowQuestionForm(true);
  };

  const handleDeleteQuestion = (questionId) => {
    const question = quizQuestions.find(q => q.id === questionId);
    if (!question) return;
    
    showDeleteConfirm(
      'Question',
      question.question_text,
      [
        'This will also delete all Answer Sheets submitted for this question (all users).'
      ],
      () => performDeleteQuestion(questionId)
    );
  };

  const performDeleteQuestion = async (questionId) => {
    setDeleteConfirm(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const result = await QuizService.deleteQuestion(questionId, selectedQuiz?.id);
      if (result.success) {
        toastService.success('Question deleted successfully');
        await loadQuizQuestions(selectedQuiz?.id);
      } else {
        toastService.error(result.message || 'Failed to delete question');
      }
    } catch (error) {
      console.error('Delete question error:', error);
      toastService.error('Failed to delete question. Please try again.');
    } finally {
      setDeleteConfirm(prev => ({ ...prev, isOpen: false, isDeleting: false }));
    }
  };

  const cancelEditQuestion = () => {
    setEditingQuestion(null);
    setIsEditMode(false);
    resetQuestionForm();
    setShowQuestionForm(false);
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    
    // Reset the fetch ref to allow new page fetch
    didFetchQuizzes.current = false;
    
    setCurrentPage(newPage);
    loadQuizzes(newPage, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    
    // Reset the fetch ref to allow new fetch
    didFetchQuizzes.current = false;
    
    loadQuizzes(1, newItemsPerPage);
  };

  // User pagination handlers
  const handleUserPageChange = (newPage) => {
    if (newPage < 1 || newPage > userTotalPages) return;
    
    setUserCurrentPage(newPage);
    
    // Reload data based on current user list type
    if (userListType === 'users') {
      loadUsers(newPage, userItemsPerPage);
    } else if (userListType === 'admins') {
      loadAdmins(newPage, userItemsPerPage);
    } else {
      // For 'all', load both users and admins
      loadUsers(newPage, userItemsPerPage);
      loadAdmins(newPage, userItemsPerPage);
    }
  };

  const handleUserItemsPerPageChange = (newItemsPerPage) => {
    setUserItemsPerPage(newItemsPerPage);
    setUserCurrentPage(1); // Reset to first page
    
    // Reload data based on current user list type
    if (userListType === 'users') {
      loadUsers(1, newItemsPerPage);
    } else if (userListType === 'admins') {
      loadAdmins(1, newItemsPerPage);
    } else {
      // For 'all', load both users and admins
      loadUsers(1, newItemsPerPage);
      loadAdmins(1, newItemsPerPage);
    }
  };

  // Option management functions for question form
  const addOption = () => {
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, createEmptyOption()]
    });
  };

  const removeOption = (index) => {
    const removed = questionForm.options[index];
    if (removed?.image_preview) URL.revokeObjectURL(removed.image_preview);
    if (optionImageInputRefs.current[index]) {
      optionImageInputRefs.current[index].value = '';
      delete optionImageInputRefs.current[index];
    }

    const newOptions = questionForm.options.filter((_, i) => i !== index);
    const remapIndex = (value) => {
      const numeric = Number(value);
      if (Number.isNaN(numeric) || numeric === index) return null;
      return String(numeric > index ? numeric - 1 : numeric);
    };

    const nextCorrectAnswer = questionForm.question_type === 'radio'
      ? (remapIndex(questionForm.correct_answer) || '')
      : questionForm.correct_answer;
    const nextCorrectAnswers = Array.isArray(questionForm.correct_answers)
      ? questionForm.correct_answers.map(remapIndex).filter(value => value !== null)
      : [];

    setQuestionForm({
      ...questionForm,
      options: newOptions,
      correct_answer: nextCorrectAnswer,
      correct_answers: nextCorrectAnswers
    });
  };

  const updateOption = (index, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = { ...newOptions[index], option_text: value };
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const updateOptionImage = (index, file) => {
    const newOptions = [...questionForm.options];
    const previousPreview = newOptions[index]?.image_preview;
    if (previousPreview) URL.revokeObjectURL(previousPreview);

    newOptions[index] = {
      ...newOptions[index],
      image_file: file || null,
      image_preview: file ? URL.createObjectURL(file) : '',
      image_url: file ? null : newOptions[index]?.image_url || null
    };

    setQuestionForm({
      ...questionForm,
      has_images: true,
      options: newOptions
    });
  };

  const clearOptionImage = (index) => {
    const newOptions = [...questionForm.options];
    if (newOptions[index]?.image_preview) URL.revokeObjectURL(newOptions[index].image_preview);
    if (optionImageInputRefs.current[index]) {
      optionImageInputRefs.current[index].value = '';
    }
    newOptions[index] = {
      ...newOptions[index],
      image_file: null,
      image_preview: '',
      image_url: null
    };
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const updateQuestionImage = (file) => {
    if (questionForm.question_image_preview) {
      URL.revokeObjectURL(questionForm.question_image_preview);
    }

    setQuestionForm({
      ...questionForm,
      has_images: true,
      question_image_file: file || null,
      question_image_preview: file ? URL.createObjectURL(file) : '',
      question_image_url: file ? null : questionForm.question_image_url
    });
  };

  const clearQuestionImage = () => {
    if (questionForm.question_image_preview) {
      URL.revokeObjectURL(questionForm.question_image_preview);
    }
    if (questionImageInputRef.current) {
      questionImageInputRef.current.value = '';
    }
    setQuestionForm({
      ...questionForm,
      question_image_file: null,
      question_image_preview: '',
      question_image_url: null
    });
  };

  const setQuestionImageMode = (enabled) => {
    if (enabled) {
      setQuestionForm({
        ...questionForm,
        has_images: true
      });
      return;
    }

    if (questionForm.question_image_preview) {
      URL.revokeObjectURL(questionForm.question_image_preview);
    }

    const optionsWithoutImages = (questionForm.options || []).map((option) => {
      if (option.image_preview) URL.revokeObjectURL(option.image_preview);
      return {
        ...option,
        image_file: null,
        image_preview: '',
        image_url: null
      };
    });

    if (questionImageInputRef.current) questionImageInputRef.current.value = '';
    Object.values(optionImageInputRefs.current).forEach((input) => {
      if (input) input.value = '';
    });

    setQuestionForm({
      ...questionForm,
      has_images: false,
      question_image_file: null,
      question_image_preview: '',
      question_image_url: null,
      options: optionsWithoutImages
    });
  };

  // Reviewer functions
  const loadReviewerEvents = async () => {
    setReviewerLoading(true);
    setReviewerError(null);
    try {
      const result = await reviewerService.getEvents();
      if (result.success) {
        setReviewerEvents(result.data || []);
      } else {
        setReviewerError(result.message);
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        }
      }
    } catch (error) {
      setReviewerError('Failed to load events');
      toastService.error('Failed to load events');
    } finally {
      setReviewerLoading(false);
    }
  };

  const loadReviewerQuizzes = async (eventId) => {
    setReviewerLoading(true);
    setReviewerError(null);
    try {
      const result = await reviewerService.getQuizzesByEvent(eventId);
      if (result.success) {
        setReviewerQuizzes(result.data || []);
        setReviewerView('quizzes');
      } else {
        setReviewerError(result.message);
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        }
      }
    } catch (error) {
      setReviewerError('Failed to load quizzes');
      toastService.error('Failed to load quizzes');
    } finally {
      setReviewerLoading(false);
    }
  };

  const loadReviewerUsers = async (quizId, page = reviewerUsersPage, limit = reviewerUsersLimit) => {
    setReviewerLoading(true);
    setReviewerError(null);
    try {
      const result = await reviewerService.getUsersByQuiz(quizId, page, limit);
      if (result.success) {
        const usersData = Array.isArray(result.data)
          ? result.data
          : (result.data?.data || result.data?.users || []);

        setReviewerUsers(usersData || []);

        const pagination = result.data?.pagination || result.raw?.data?.pagination;
        if (pagination) {
          setReviewerUsersTotalPages(pagination.totalPages || pagination.total_pages || 1);
          setReviewerUsersTotalCount(pagination.total || pagination.total_count || pagination.count || 0);
        } else {
          setReviewerUsersTotalPages((usersData || []).length === limit ? page + 1 : page);
          setReviewerUsersTotalCount((usersData || []).length);
        }

        setReviewerUsersPage(page);
        setReviewerUsersLimit(limit);
        setReviewerView('users');
      } else {
        setReviewerError(result.message);
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        }
      }
    } catch (error) {
      setReviewerError('Failed to load users');
      toastService.error('Failed to load users');
    } finally {
      setReviewerLoading(false);
    }
  };

  const handleReviewerUsersPageChange = (newPage) => {
    if (newPage < 1 || newPage > reviewerUsersTotalPages) return;
    if (!selectedReviewerQuiz?.id) return;
    loadReviewerUsers(selectedReviewerQuiz.id, newPage, reviewerUsersLimit);
  };

  const handleReviewerUsersLimitChange = (newLimit) => {
    const limit = Number(newLimit);
    if (!Number.isFinite(limit) || limit <= 0) return;
    if (!selectedReviewerQuiz?.id) return;
    loadReviewerUsers(selectedReviewerQuiz.id, 1, limit);
  };

  const getReviewerFilteredUsers = () => {
    if (reviewerUsersDesignationFilter === 'all') return reviewerUsers;
    return reviewerUsers.filter((u) => (u?.designation || 'Unknown') === reviewerUsersDesignationFilter);
  };

  const loadReviewerAnswers = async (quizId, userId) => {
    setReviewerLoading(true);
    setReviewerError(null);
    try {
      const result = await reviewerService.getAnswersByQuizUser(quizId, userId);
      console.log('🔍 Reviewer Answers API Response:', result);
      if (result.success) {
        console.log('🔍 Reviewer Answers Data:', result.data);
        console.log('🔍 Answer Sheet IDs:', result.data?.map(a => a.answer_sheet_id));
        
        // Log all fields for each answer to find available ID fields
        result.data?.forEach((answer, index) => {
          console.log(`🔍 Answer ${index} fields:`, Object.keys(answer));
          console.log(`🔍 Answer ${index} details:`, {
            answer_sheet_id: answer.answer_sheet_id,
            id: answer.id,
            answer_id: answer.answer_id,
            sheet_id: answer.sheet_id,
            question_id: answer.question_id
          });
        });
        
        setReviewerAnswers(result.data || []);
        setReviewerView('answers');
      } else {
        setReviewerError(result.message);
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        }
      }
    } catch (error) {
      setReviewerError('Failed to load answers');
      toastService.error('Failed to load answers');
    } finally {
      setReviewerLoading(false);
    }
  };

  const handleViewQuizzes = (event) => {
    setSelectedReviewerEvent(event);
    loadReviewerQuizzes(event.id);
  };

  const handleViewUsers = (quiz) => {
    setSelectedReviewerQuiz(quiz);
    setReviewerUsersPage(1);
    loadReviewerUsers(quiz.id, 1, reviewerUsersLimit);
  };

  const handleViewAnswers = (user) => {
    setSelectedReviewerUser(user);
    loadReviewerAnswers(selectedReviewerQuiz.id, user.user_id);
  };

  const handleBackToEvents = () => {
    setReviewerView('events');
    setSelectedReviewerEvent(null);
    setReviewerQuizzes([]);
  };

  const handleBackToReviewerQuizzes = () => {
    setReviewerView('quizzes');
    setSelectedReviewerQuiz(null);
    setReviewerUsers([]);
  };

  const handleBackToUsers = () => {
    setReviewerView('users');
    setSelectedReviewerUser(null);
    setReviewerAnswers([]);
    // Refresh users list to update has_pending_review status
    if (selectedReviewerQuiz) {
      loadReviewerUsers(selectedReviewerQuiz.id, reviewerUsersPage, reviewerUsersLimit);
    }
  };

  const handleExport = async (eventId) => {
    try {
      setExporting(true);

      const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN) || localStorage.getItem('access_token');
      const exportEndpoint = formatEndpoint(API_CONFIG.ENDPOINTS.EXPORT_EXCEL, { eventId });
      console.log('🔍 Export token:', token ? 'exists' : 'missing');
      console.log('🔍 Export endpoint:', getApiUrl(exportEndpoint));
      
      const res = await axios.get(getApiUrl(exportEndpoint), {
        responseType: "blob",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // ✅ success => download
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="(.+)"/);
      const filename = match?.[1] || `event_results_${eventId}.xlsx`;

      const blob = new Blob([res.data], { type: res.data.type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      toastService.success("Export downloaded");
    } catch (err) {
      console.log('🔍 Export error:', err);
      console.log('🔍 Export error response:', err?.response);
      console.log('🔍 Export error status:', err?.response?.status);
      
      // ✅ error may come as JSON or blob
      const data = err?.response?.data;

      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const json = JSON.parse(text);
          toastService.error(json?.message || "Export failed");
        } catch {
          toastService.error("Export failed");
        }
      } else {
        toastService.error(err?.response?.data?.message || "Export failed");
      }
    } finally {
      setExporting(false);
    }
  };

  const getFilteredAnswers = () => {
    if (answerFilter === 'pending') {
      return reviewerAnswers.filter(answer => !answer.is_graded);
    } else if (answerFilter === 'graded') {
      return reviewerAnswers.filter(answer => answer.is_graded);
    }
    return reviewerAnswers;
  };

  // Helper function to get the correct answer ID
  const getAnswerId = (answer) => {
    // First try to find a proper ID field
    const id = answer.id || answer.answer_id || answer.sheet_id || answer.answer_sheet_id;
    if (id) {
      return id;
    }
    
    // If no proper ID exists, create a composite key from question_id and other unique fields
    // This ensures we can still track the answer for grading
    const compositeKey = `q${answer.question_id}-u${answer.user_id || 'unknown'}-${answer.question_text?.slice(0, 20) || 'unknown'}`;
    console.log('🔍 Created composite key for answer without ID:', compositeKey, answer);
    return compositeKey;
  };

  // Grading functions
  const handleGradeAnswer = async (answerSheetId) => {
    console.log('🔍 handleGradeAnswer called with answerSheetId:', answerSheetId);
    console.log('🔍 typeof answerSheetId:', typeof answerSheetId);
    
    const points = gradingInputs[answerSheetId];
    const notes = gradingNotes[answerSheetId] || '';
    
    console.log('🔍 gradingInputs:', gradingInputs);
    console.log('🔍 points for this answerSheetId:', points);
    
    // Validation
    if (answerSheetId === null || answerSheetId === undefined || answerSheetId === 'null' || answerSheetId === '') {
      console.error('❌ Invalid answerSheetId:', answerSheetId);
      toastService.error('Invalid answer sheet ID. Cannot grade this answer.');
      return;
    }
    
    // Check if this is a composite key (answers without proper IDs cannot be graded via API)
    if (typeof answerSheetId === 'string' && answerSheetId.startsWith('q')) {
      console.error('❌ Cannot grade answer with composite key - no proper ID available:', answerSheetId);
      toastService.error('This answer cannot be graded because it has no valid ID. Please contact the administrator.');
      return;
    }
    
    if (points === undefined || points === null || points === '') {
      toastService.error('Please enter points');
      return;
    }
    
    const numericPoints = parseFloat(points);
    if (isNaN(numericPoints)) {
      toastService.error('Points must be a valid number');
      return;
    }
    
    const answer = reviewerAnswers.find(a => a.answer_sheet_id === answerSheetId);
    if (numericPoints < 0) {
      toastService.error('Points cannot be negative');
      return;
    }
    
    if (numericPoints > answer.points_possible) {
      toastService.error(`Points cannot exceed maximum (${answer.points_possible})`);
      return;
    }
    
    // Set loading state
    setGradingAnswers(prev => ({ ...prev, [answerSheetId]: true }));
    
    try {
      const result = await reviewerService.gradeAnswer(answerSheetId, numericPoints, notes);
      
      if (result.success) {
        // Success toast
        toastService.success('Answer sheet graded successfully');
        
        // Update answer locally
        setReviewerAnswers(prev => prev.map(answer => {
          if (answer.answer_sheet_id === answerSheetId) {
            return {
              ...answer,
              points_awarded: result.data?.points || numericPoints,
              is_graded: true,
              reviewer_notes: notes
            };
          }
          return answer;
        }));
        
        // Clear grading inputs for this answer
        setGradingInputs(prev => {
          const newInputs = { ...prev };
          delete newInputs[answerSheetId];
          return newInputs;
        });
        setGradingNotes(prev => {
          const newNotes = { ...prev };
          delete newNotes[answerSheetId];
          return newNotes;
        });
        
      } else {
        // Handle API errors
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        } else if (result.raw?.status_code === 400 || result.raw?.status_code === 422) {
          toastService.error(result.message || 'Invalid points value');
        } else if (result.raw?.status_code === 404) {
          toastService.error('Answer sheet not found');
        } else {
          toastService.error(result.message || 'Failed to save grade');
        }
      }
    } catch (error) {
      toastService.error('Network error. Please try again.');
    } finally {
      // Clear loading state
      setGradingAnswers(prev => ({ ...prev, [answerSheetId]: false }));
    }
  };

  const handlePointsChange = (answerSheetId, value) => {
    setGradingInputs(prev => ({ ...prev, [answerSheetId]: value }));
  };

  const handleNotesChange = (answerSheetId, value) => {
    setGradingNotes(prev => ({ ...prev, [answerSheetId]: value }));
  };

  // Initialize grading inputs when answers load
  useEffect(() => {
    if (reviewerAnswers.length > 0) {
      const initialInputs = {};
      const initialNotes = {};
      
      reviewerAnswers.forEach(answer => {
        if (!answer.is_graded) {
          const answerId = getAnswerId(answer);
          console.log('🔍 Using answerId as key:', answerId, 'for answer:', answer);
          
          // Only initialize grading inputs for answers with proper IDs
          const hasProperId = answer.id || answer.answer_id || answer.sheet_id || answer.answer_sheet_id;
          if (hasProperId && answerId) {
            initialInputs[answerId] = answer.points_awarded.toString();
            initialNotes[answerId] = answer.reviewer_notes || '';
          } else {
            console.warn('⚠️ Skipping grading initialization for answer without proper ID:', answer);
          }
        }
      });
      
      console.log('🔍 Initial grading inputs:', initialInputs);
      setGradingInputs(initialInputs);
      setGradingNotes(initialNotes);
    }
  }, [reviewerAnswers]);

  // User Results functions
  const loadUserResultsEvents = async (page = eventsPage, limit = eventsLimit) => {
    setUserResultsLoading(true);
    setUserResultsError(null);
    
    try {
      const result = await userResultsService.getEvents(page, limit);
      if (result.success) {
        setUserResultsEvents(result.data || []);
        setEventsTotal(result.total || 0);
        setEventsPage(result.page || 1);
        
        // Auto-select first event if none selected and events exist
        if (!selectedUserResultsEvent && result.data && result.data.length > 0) {
          handleSelectEvent(result.data[0]);
        }
      } else {
        setUserResultsError(result.message);
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        }
      }
    } catch (error) {
      setUserResultsError('Failed to load events');
      toastService.error('Failed to load events');
    } finally {
      setUserResultsLoading(false);
    }
  };

  const loadEventLeaderboard = async (eventId, page = leaderboardPage, limit = leaderboardLimit) => {
    setUserResultsLoading(true);
    setUserResultsError(null);
    
    try {
      const result = await userResultsService.getEventUsers(eventId, page, limit);
      if (result.success) {
        setUserResultsLeaderboard(result.data || []);
        setLeaderboardTotal(result.total || 0);
        setLeaderboardPage(result.page || 1);
      } else {
        setUserResultsError(result.message);
        if (result.raw?.status_code === 401) {
          toastService.error('Session expired');
          handleLogout();
        }
      }
    } catch (error) {
      setUserResultsError('Failed to load leaderboard');
      toastService.error('Failed to load leaderboard');
    } finally {
      setUserResultsLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedUserResultsEvent(event);
    setLeaderboardPage(1); // Reset leaderboard page when selecting new event
    loadEventLeaderboard(event.id, 1, leaderboardLimit);
  };

  const handleEventsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(eventsTotal / eventsLimit)) {
      setEventsPage(newPage);
      loadUserResultsEvents(newPage, eventsLimit);
    }
  };

  const handleLeaderboardPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(leaderboardTotal / leaderboardLimit)) {
      setLeaderboardPage(newPage);
      if (selectedUserResultsEvent) {
        loadEventLeaderboard(selectedUserResultsEvent.id, newPage, leaderboardLimit);
      }
    }
  };

  const handleLeaderboardItemsPerPageChange = (newLimit) => {
    setLeaderboardLimit(newLimit);
    setLeaderboardPage(1); // Reset to first page
    if (selectedUserResultsEvent) {
      loadEventLeaderboard(selectedUserResultsEvent.id, 1, newLimit);
    }
  };

  const calculateRank = (index) => {
    return (leaderboardPage - 1) * leaderboardLimit + index + 1;
  };

  const loadLoginBanner = async () => {
    setLoginBannerLoading(true);
    try {
      const result = await AppSettingsService.getLoginBanner();
      if (result.success) {
        setLoginBannerUrl(result.data?.image_url || '');
      }
    } catch (error) {
      console.error('Error loading login banner:', error);
    } finally {
      setLoginBannerLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (loginBannerPreview) URL.revokeObjectURL(loginBannerPreview);
    };
  }, [loginBannerPreview]);

  const getLoginBannerImageSrc = () => {
    if (loginBannerPreview) return loginBannerPreview;
    if (loginBannerUrl) return AppSettingsService.getMediaUrl(loginBannerUrl);
    return DefaultQuizBanner;
  };

  const handleLoginBannerFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (file && !file.type.startsWith('image/')) {
      toastService.error('Please select an image file');
      setLoginBannerFile(null);
      if (loginBannerInputRef.current) loginBannerInputRef.current.value = '';
      return;
    }

    setLoginBannerFile(file);
    setLoginBannerPreview(file ? URL.createObjectURL(file) : '');
  };

  const resetLoginBannerFile = () => {
    setLoginBannerFile(null);
    setLoginBannerPreview('');
    if (loginBannerInputRef.current) loginBannerInputRef.current.value = '';
  };

  const handleUploadLoginBanner = async (event) => {
    event.preventDefault();

    if (!loginBannerFile) {
      toastService.error('Please select an image file');
      return;
    }

    setLoginBannerSaving(true);
    try {
      const result = await AppSettingsService.uploadLoginBanner(loginBannerFile);
      if (result.success) {
        setLoginBannerUrl(result.data?.image_url || '');
        resetLoginBannerFile();
        toastService.success('Login banner updated');
      } else {
        toastService.error(result.message || 'Failed to update login banner');
      }
    } catch (error) {
      console.error('Error uploading login banner:', error);
      toastService.error('Failed to update login banner');
    } finally {
      setLoginBannerSaving(false);
    }
  };

  const handleClearLoginBanner = async () => {
    setLoginBannerSaving(true);
    try {
      const result = await AppSettingsService.clearLoginBanner();
      if (result.success) {
        setLoginBannerUrl('');
        resetLoginBannerFile();
        toastService.success('Default login banner restored');
      } else {
        toastService.error(result.message || 'Failed to restore default banner');
      }
    } catch (error) {
      console.error('Error clearing login banner:', error);
      toastService.error('Failed to restore default banner');
    } finally {
      setLoginBannerSaving(false);
    }
  };

  const loadQuizInstructions = async () => {
    setQuizInstructionsLoading(true);
    try {
      const result = await AppSettingsService.getQuizInstructions();
      if (result.success) {
        setQuizInstructions(normalizeInstructionForm(result.data));
      }
    } catch (error) {
      console.error('Error loading quiz instructions:', error);
    } finally {
      setQuizInstructionsLoading(false);
    }
  };

  const updateQuizInstructionField = (field, value) => {
    setQuizInstructions((prev) => ({
      ...normalizeInstructionForm(prev || EMPTY_QUIZ_INSTRUCTIONS),
      [field]: value
    }));
  };

  const updateInstructionSection = (index, updates) => {
    setQuizInstructions((prev) => {
      const next = normalizeInstructionForm(prev || EMPTY_QUIZ_INSTRUCTIONS);
      const sections = [...next.sections];
      sections[index] = {
        ...createInstructionSection(),
        ...sections[index],
        ...updates
      };
      return { ...next, sections };
    });
  };

  const addInstructionSection = () => {
    setQuizInstructions((prev) => {
      const next = normalizeInstructionForm(prev || EMPTY_QUIZ_INSTRUCTIONS);
      return {
        ...next,
        sections: [
          ...next.sections,
          {
            ...createInstructionSection(),
            title: `${next.sections.length + 1}. New Instruction`
          }
        ]
      };
    });
  };

  const removeInstructionSection = (index) => {
    setQuizInstructions((prev) => {
      const next = normalizeInstructionForm(prev || EMPTY_QUIZ_INSTRUCTIONS);
      return {
        ...next,
        sections: next.sections.filter((_, sectionIndex) => sectionIndex !== index)
      };
    });
  };

  const handleSaveQuizInstructions = async (event) => {
    event.preventDefault();

    const payload = normalizeInstructionForm(quizInstructions || EMPTY_QUIZ_INSTRUCTIONS);
    if (!payload.title.trim()) {
      toastService.error('Instruction title is required');
      return;
    }

    setQuizInstructionsSaving(true);
    try {
      const result = await AppSettingsService.updateQuizInstructions(payload);
      if (result.success) {
        setQuizInstructions(normalizeInstructionForm(result.data));
        toastService.success('Quiz instructions updated');
      } else {
        toastService.error(result.message || 'Failed to update quiz instructions');
      }
    } catch (error) {
      console.error('Error saving quiz instructions:', error);
      toastService.error('Failed to update quiz instructions');
    } finally {
      setQuizInstructionsSaving(false);
    }
  };

  const handleResetQuizInstructions = async () => {
    setQuizInstructionsSaving(true);
    try {
      const result = await AppSettingsService.resetQuizInstructions();
      if (result.success) {
        setQuizInstructions(normalizeInstructionForm(result.data));
        toastService.success('Default quiz instructions restored');
      } else {
        toastService.error(result.message || 'Failed to restore default instructions');
      }
    } catch (error) {
      console.error('Error resetting quiz instructions:', error);
      toastService.error('Failed to restore default instructions');
    } finally {
      setQuizInstructionsSaving(false);
    }
  };

  const loadSecuritySettings = async () => {
    setSecuritySettingsLoading(true);
    try {
      const result = await AppSettingsService.getSecuritySettings();
      if (result.success) {
        setSecuritySettings(normalizeSecurityForm(result.data));
      }
    } catch (error) {
      console.error('Error loading security settings:', error);
    } finally {
      setSecuritySettingsLoading(false);
    }
  };

  const loadSessionPolicy = async () => {
    setSessionPolicyLoading(true);
    try {
      const result = await AppSettingsService.getSessionPolicy();
      if (result.success) {
        setSessionPolicy(normalizeSessionPolicy(result.data));
      }
    } catch (error) {
      console.error('Error loading session policy:', error);
    } finally {
      setSessionPolicyLoading(false);
    }
  };

  const refreshSecurityControlSettings = () => {
    loadSecuritySettings();
    loadSessionPolicy();
  };

  const updateSecuritySettingField = (field, value) => {
    setSecuritySettings((prev) => ({
      ...normalizeSecurityForm(prev || DEFAULT_SECURITY_SETTINGS),
      [field]: value
    }));
  };

  const updateSecurityRule = (ruleKey, updates) => {
    setSecuritySettings((prev) => {
      const next = normalizeSecurityForm(prev || DEFAULT_SECURITY_SETTINGS);
      return {
        ...next,
        rules: {
          ...next.rules,
          [ruleKey]: {
            ...next.rules[ruleKey],
            ...updates
          }
        }
      };
    });
  };

  const updateSuspiciousTypingSetting = (field, value) => {
    setSecuritySettings((prev) => {
      const next = normalizeSecurityForm(prev || DEFAULT_SECURITY_SETTINGS);
      return {
        ...next,
        suspiciousTyping: {
          ...next.suspiciousTyping,
          [field]: value
        }
      };
    });
  };

  const updateSessionPolicyField = (field, value) => {
    setSessionPolicy((prev) => ({
      ...normalizeSessionPolicy(prev || DEFAULT_SESSION_POLICY),
      [field]: value
    }));
  };

  const handleSaveSecuritySettings = async (event) => {
    event.preventDefault();

    const securityPayload = normalizeSecurityForm(securitySettings || DEFAULT_SECURITY_SETTINGS);
    const sessionPayload = normalizeSessionPolicy(sessionPolicy || DEFAULT_SESSION_POLICY);

    setSecuritySettingsSaving(true);
    try {
      const [securityResult, sessionResult] = await Promise.all([
        AppSettingsService.updateSecuritySettings(securityPayload),
        AppSettingsService.updateSessionPolicy(sessionPayload)
      ]);

      if (securityResult.success && sessionResult.success) {
        setSecuritySettings(normalizeSecurityForm(securityResult.data));
        setSessionPolicy(normalizeSessionPolicy(sessionResult.data));
        toastService.success('Security settings updated');
      } else {
        toastService.error(
          securityResult.message ||
          sessionResult.message ||
          'Failed to update security settings'
        );
      }
    } catch (error) {
      console.error('Error saving security settings:', error);
      toastService.error('Failed to update security settings');
    } finally {
      setSecuritySettingsSaving(false);
    }
  };

  const handleResetSecuritySettings = async () => {
    setSecuritySettingsSaving(true);
    try {
      const [securityResult, sessionResult] = await Promise.all([
        AppSettingsService.resetSecuritySettings(),
        AppSettingsService.resetSessionPolicy()
      ]);

      if (securityResult.success && sessionResult.success) {
        setSecuritySettings(normalizeSecurityForm(securityResult.data));
        setSessionPolicy(normalizeSessionPolicy(sessionResult.data));
        toastService.success('Default security settings restored');
      } else {
        toastService.error(
          securityResult.message ||
          sessionResult.message ||
          'Failed to restore default security settings'
        );
      }
    } catch (error) {
      console.error('Error resetting security settings:', error);
      toastService.error('Failed to restore default security settings');
    } finally {
      setSecuritySettingsSaving(false);
    }
  };

  // Load reviewer events when reviewer tab is activated
  useEffect(() => {
    if (activeTab === 'reviewer' && reviewerEvents.length === 0) {
      loadReviewerEvents();
    }
  }, [activeTab]);

  // Load user results events when user results tab is activated
  useEffect(() => {
    if (activeTab === 'userResults' && userResultsEvents.length === 0) {
      loadUserResultsEvents();
    }
  }, [activeTab]);

  const instructionForm = normalizeInstructionForm(quizInstructions || EMPTY_QUIZ_INSTRUCTIONS);
  const securityForm = normalizeSecurityForm(securitySettings || DEFAULT_SECURITY_SETTINGS);
  const sessionPolicyForm = normalizeSessionPolicy(sessionPolicy || DEFAULT_SESSION_POLICY);

  return (
    <div className="admin-dashboard">
      <div className="page-title-section">
        {/* <h1>Admin Dashboard</h1> */}
      </div>
      
      <div className="admin-tabs">
        <button 
          type="button"
          title="Users Management"
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          type="button"
          title="Designations"
          className={`tab-btn ${activeTab === 'designations' ? 'active' : ''}`}
          onClick={() => setActiveTab('designations')}
        >
          Designations
        </button>
        <button 
          type="button"
          title="Events"
          className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button 
          type="button"
          title="Quiz Builder"
          className={`tab-btn ${activeTab === 'builder' ? 'active' : ''}`}
          onClick={() => setActiveTab('builder')}
        >
          Quiz
        </button>
        <button 
          type="button"
          title="Review Answer Sheets"
          className={`tab-btn ${activeTab === 'reviewer' ? 'active' : ''}`}
          onClick={() => setActiveTab('reviewer')}
        >
          Review
        </button>
        <button 
          type="button"
          title="User Results"
          className={`tab-btn ${activeTab === 'userResults' ? 'active' : ''}`}
          onClick={() => setActiveTab('userResults')}
        >
          Results
        </button>
        <button
          type="button"
          title="Login Banner"
          className={`tab-btn ${activeTab === 'branding' ? 'active' : ''}`}
          onClick={() => setActiveTab('branding')}
        >
          Banner
        </button>
        <button
          type="button"
          title="Instructions"
          className={`tab-btn ${activeTab === 'instructions' ? 'active' : ''}`}
          onClick={() => setActiveTab('instructions')}
        >
          Instructions
        </button>
        <button
          type="button"
          title="Security"
          className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>

      
        
      </div>

      <div className="admin-content">
        {activeTab === 'branding' && (
          <div className="branding-section">
            <div className="section-header">
              <h2>Login Banner</h2>
            </div>

            <div className="branding-layout">
              <div className="login-banner-preview">
                {loginBannerLoading ? (
                  <div className="banner-preview-loading">Loading banner...</div>
                ) : (
                  <img
                    src={getLoginBannerImageSrc()}
                    alt="Login banner preview"
                    onError={(event) => {
                      event.currentTarget.src = DefaultQuizBanner;
                    }}
                  />
                )}
              </div>

              <form className="branding-form" onSubmit={handleUploadLoginBanner}>
                <div className="branding-status">
                  {loginBannerUrl ? 'Custom banner active' : 'Default banner active'}
                </div>

                <div className="form-group">
                  <label htmlFor="loginBannerImage">Banner Image</label>
                  <input
                    id="loginBannerImage"
                    ref={loginBannerInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleLoginBannerFileChange}
                    className="file-input"
                  />
                </div>

                <div className="branding-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!loginBannerFile || loginBannerSaving}
                  >
                    {loginBannerSaving ? 'Uploading...' : 'Upload Banner'}
                  </button>
                  {loginBannerFile && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={resetLoginBannerFile}
                      disabled={loginBannerSaving}
                    >
                      Cancel
                    </button>
                  )}
                  {loginBannerUrl && (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={handleClearLoginBanner}
                      disabled={loginBannerSaving}
                    >
                      Use Default
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'instructions' && (
          <div className="instructions-editor-section">
            <div className="section-header">
              <h2>Quiz Instructions</h2>
              <button
                type="button"
                className="btn-secondary"
                onClick={loadQuizInstructions}
                disabled={quizInstructionsLoading || quizInstructionsSaving}
              >
                Refresh
              </button>
            </div>

            {quizInstructionsLoading && !quizInstructions ? (
              <div className="loading">Loading instructions...</div>
            ) : (
              <form className="instructions-editor-form" onSubmit={handleSaveQuizInstructions}>
                <div className="instructions-form-grid">
                  <div className="form-group">
                    <label htmlFor="instructionTitle">Header Title</label>
                    <input
                      id="instructionTitle"
                      type="text"
                      value={instructionForm.title}
                      onChange={(event) => updateQuizInstructionField('title', event.target.value)}
                      placeholder="Online Quiz Instructions & Agreement"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="instructionBreakdown">Breakdown Text</label>
                    <input
                      id="instructionBreakdown"
                      type="text"
                      value={instructionForm.breakdown_text}
                      onChange={(event) => updateQuizInstructionField('breakdown_text', event.target.value)}
                      placeholder="70% Common Section, 30% role-Specific"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="estimatedTimeLabel">Estimated Time Label</label>
                    <input
                      id="estimatedTimeLabel"
                      type="text"
                      value={instructionForm.estimated_time_label}
                      onChange={(event) => updateQuizInstructionField('estimated_time_label', event.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="estimatedTimeValue">Estimated Time Fallback</label>
                    <input
                      id="estimatedTimeValue"
                      type="text"
                      value={instructionForm.estimated_time_value}
                      onChange={(event) => updateQuizInstructionField('estimated_time_value', event.target.value)}
                      placeholder="1.45 mins"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="totalMarksLabel">Total Marks Label</label>
                    <input
                      id="totalMarksLabel"
                      type="text"
                      value={instructionForm.total_marks_label}
                      onChange={(event) => updateQuizInstructionField('total_marks_label', event.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="totalMarksValue">Total Marks Fallback</label>
                    <input
                      id="totalMarksValue"
                      type="text"
                      value={instructionForm.total_marks_value}
                      onChange={(event) => updateQuizInstructionField('total_marks_value', event.target.value)}
                      placeholder="100"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="breakdownLabel">Breakdown Label</label>
                    <input
                      id="breakdownLabel"
                      type="text"
                      value={instructionForm.breakdown_label}
                      onChange={(event) => updateQuizInstructionField('breakdown_label', event.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="instructionIntro">Instruction Intro</label>
                  <textarea
                    id="instructionIntro"
                    rows={5}
                    value={instructionForm.intro_text}
                    onChange={(event) => updateQuizInstructionField('intro_text', event.target.value)}
                    placeholder="Write the opening instruction text candidates should read."
                  />
                </div>

                <div className="instructions-form-grid">
                  <div className="form-group">
                    <label htmlFor="agreementText">Agreement Checkbox Text</label>
                    <input
                      id="agreementText"
                      type="text"
                      value={instructionForm.agreement_text}
                      onChange={(event) => updateQuizInstructionField('agreement_text', event.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="agreementErrorText">Agreement Error Text</label>
                    <input
                      id="agreementErrorText"
                      type="text"
                      value={instructionForm.error_text}
                      onChange={(event) => updateQuizInstructionField('error_text', event.target.value)}
                    />
                  </div>
                </div>

                <div className="instruction-sections-header">
                  <h3>Instruction Sections</h3>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={addInstructionSection}
                  >
                    Add Section
                  </button>
                </div>

                <div className="instruction-section-list">
                  {instructionForm.sections.map((section, index) => (
                    <div className="instruction-section-editor" key={`${section.title}-${index}`}>
                      <div className="instruction-section-editor-header">
                        <h4>Section {index + 1}</h4>
                        <button
                          type="button"
                          className="btn-danger btn-sm"
                          onClick={() => removeInstructionSection(index)}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="instructions-form-grid">
                        <div className="form-group">
                          <label htmlFor={`instructionSectionTitle-${index}`}>Section Title</label>
                          <input
                            id={`instructionSectionTitle-${index}`}
                            type="text"
                            value={section.title}
                            onChange={(event) => updateInstructionSection(index, { title: event.target.value })}
                            placeholder={`${index + 1}. Instruction title`}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`instructionSectionVariant-${index}`}>Visual Style</label>
                          <select
                            id={`instructionSectionVariant-${index}`}
                            value={section.variant || 'normal'}
                            onChange={(event) => updateInstructionSection(index, { variant: event.target.value })}
                          >
                            <option value="normal">Normal</option>
                            <option value="important">Important</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor={`instructionSectionBody-${index}`}>Body Text</label>
                        <textarea
                          id={`instructionSectionBody-${index}`}
                          rows={3}
                          value={section.body || ''}
                          onChange={(event) => updateInstructionSection(index, { body: event.target.value })}
                          placeholder="Section explanation"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`instructionSectionBullets-${index}`}>Bullet Points</label>
                        <textarea
                          id={`instructionSectionBullets-${index}`}
                          rows={4}
                          value={(section.bullets || []).join('\n')}
                          onChange={(event) => updateInstructionSection(index, {
                            bullets: event.target.value
                              .split('\n')
                              .map((line) => line.trim())
                              .filter(Boolean)
                          })}
                          placeholder="One bullet per line"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleResetQuizInstructions}
                    disabled={quizInstructionsSaving}
                  >
                    Reset Default
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={quizInstructionsSaving}
                  >
                    {quizInstructionsSaving ? 'Saving...' : 'Save Instructions'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {activeTab === 'security' && (
          <div className="security-settings-section">
            <div className="section-header">
              <h2>Security Settings</h2>
              <button
                type="button"
                className="btn-secondary"
                onClick={refreshSecurityControlSettings}
                disabled={securitySettingsLoading || sessionPolicyLoading || securitySettingsSaving}
              >
                Refresh
              </button>
            </div>

            {(securitySettingsLoading || sessionPolicyLoading) && (!securitySettings || !sessionPolicy) ? (
              <div className="loading">Loading security settings...</div>
            ) : (
              <form className="security-settings-form" onSubmit={handleSaveSecuritySettings}>
                <div className="security-global-row">
                  <label className="security-toggle">
                    <input
                      type="checkbox"
                      checked={securityForm.autoSubmit}
                      onChange={(event) => updateSecuritySettingField('autoSubmit', event.target.checked)}
                    />
                    <span>
                      <strong>Auto-submit when a rule reaches its limit</strong>
                      <small>When off, violations are warned and counted but do not submit the quiz.</small>
                    </span>
                  </label>
                </div>

                <div className="security-rules-list">
                  {Object.entries(SECURITY_RULE_META).map(([ruleKey, meta]) => {
                    const rule = securityForm.rules[ruleKey];
                    return (
                      <div className="security-rule-row" key={ruleKey}>
                        <label className="security-toggle">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={(event) => updateSecurityRule(ruleKey, { enabled: event.target.checked })}
                          />
                          <span>
                            <strong>{meta.label}</strong>
                            <small>{meta.description}</small>
                          </span>
                        </label>

                        <div className="security-limit-control">
                          <label htmlFor={`securityMax-${ruleKey}`}>Max</label>
                          <input
                            id={`securityMax-${ruleKey}`}
                            type="number"
                            min="1"
                            max="50"
                            value={rule.max}
                            disabled={!rule.enabled}
                            onChange={(event) => updateSecurityRule(ruleKey, {
                              max: parseInt(event.target.value, 10) || 1
                            })}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="security-advanced">
                  <h3>Suspicious Typing Threshold</h3>
                  <div className="security-advanced-grid">
                    <div className="form-group">
                      <label htmlFor="typingCharsPerSecond">Characters Per Second</label>
                      <input
                        id="typingCharsPerSecond"
                        type="number"
                        min="10"
                        max="1000"
                        value={securityForm.suspiciousTyping.charsPerSecond}
                        onChange={(event) => updateSuspiciousTypingSetting(
                          'charsPerSecond',
                          parseInt(event.target.value, 10) || DEFAULT_SECURITY_SETTINGS.suspiciousTyping.charsPerSecond
                        )}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="typingMinChars">Minimum Characters</label>
                      <input
                        id="typingMinChars"
                        type="number"
                        min="1"
                        max="100"
                        value={securityForm.suspiciousTyping.minChars}
                        onChange={(event) => updateSuspiciousTypingSetting(
                          'minChars',
                          parseInt(event.target.value, 10) || DEFAULT_SECURITY_SETTINGS.suspiciousTyping.minChars
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="security-advanced">
                  <h3>Exam Session Resume Policy</h3>
                  <div className="security-global-row">
                    <label className="security-toggle">
                      <input
                        type="checkbox"
                        checked={sessionPolicyForm.resume_enabled}
                        onChange={(event) => updateSessionPolicyField('resume_enabled', event.target.checked)}
                      />
                      <span>
                        <strong>Allow candidates to resume an in-progress exam</strong>
                        <small>Re-entry keeps the same attempt, question order, answers, and remaining time.</small>
                      </span>
                    </label>
                  </div>

                  <div className="security-advanced-grid">
                    <div className="form-group">
                      <label htmlFor="sessionMaxReconnects">Max Reconnects</label>
                      <input
                        id="sessionMaxReconnects"
                        type="number"
                        min="0"
                        max="50"
                        value={sessionPolicyForm.max_reconnects}
                        onChange={(event) => updateSessionPolicyField(
                          'max_reconnects',
                          parseInt(event.target.value, 10) || 0
                        )}
                      />
                    </div>
                  </div>

                  <div className="security-rules-list">
                    <div className="security-rule-row">
                      <label className="security-toggle">
                        <input
                          type="checkbox"
                          checked={sessionPolicyForm.flag_after_reconnect_limit}
                          onChange={(event) => updateSessionPolicyField(
                            'flag_after_reconnect_limit',
                            event.target.checked
                          )}
                        />
                        <span>
                          <strong>Flag after reconnect limit</strong>
                          <small>Keep the exam resumable, but mark the attempt for admin review.</small>
                        </span>
                      </label>
                    </div>

                    <div className="security-rule-row">
                      <label className="security-toggle">
                        <input
                          type="checkbox"
                          checked={sessionPolicyForm.auto_submit_on_reconnect_limit}
                          onChange={(event) => updateSessionPolicyField(
                            'auto_submit_on_reconnect_limit',
                            event.target.checked
                          )}
                        />
                        <span>
                          <strong>Auto-submit after reconnect limit</strong>
                          <small>Close the attempt automatically once the reconnect count is exceeded.</small>
                        </span>
                      </label>
                    </div>

                    <div className="security-rule-row">
                      <label className="security-toggle">
                        <input
                          type="checkbox"
                          checked={sessionPolicyForm.auto_submit_on_expiry}
                          onChange={(event) => updateSessionPolicyField(
                            'auto_submit_on_expiry',
                            event.target.checked
                          )}
                        />
                        <span>
                          <strong>Auto-submit when time expires offline</strong>
                          <small>If a candidate returns after time is over, finalize saved answers immediately.</small>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleResetSecuritySettings}
                    disabled={securitySettingsSaving || sessionPolicyLoading}
                  >
                    Reset Default
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={securitySettingsSaving || sessionPolicyLoading}
                  >
                    {securitySettingsSaving ? 'Saving...' : 'Save Security'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>User Management</h2>
              <div className="action-bar">
                <div className="filter-dropdown">
                  <label>Filter:</label>
                  <select
                    value={userListType}
                    onChange={(e) => setUserListType(e.target.value)}
                    className="user-filter-select"
                  >
                    <option value="all">All</option>
                    <option value="users">Users</option>
                    <option value="admins">Admins</option>
                  </select>
                </div>
                
                <div className="action-buttons">
                  {/* Bulk Upload Button */}
                  <button 
                    className="btn-utility"
                    onClick={() => setShowBulkUploadModal(true)}
                  >
                    <span className="btn-icon">📤</span>
                    Bulk Upload
                  </button>
                  
                  {/* Create Dropdown */}
                  <div className="create-dropdown">
                    <button 
                      className="btn-primary"
                      onClick={() => setShowCreateDropdown(!showCreateDropdown)}
                    >
                      <span className="btn-icon">+</span>
                      Create
                      <span className="dropdown-arrow">▼</span>
                    </button>
                    {showCreateDropdown && (
                      <div className="dropdown-menu">
                        <button 
                          className="dropdown-item"
                          onClick={() => {
                            resetUserForm();
                            setShowAdminForm(false);
                            setShowRegularUserForm(true);
                            setShowCreateDropdown(false);
                          }}
                        >
                          <span className="item-icon">👤</span>
                          Create User
                        </button>
                        <button 
                          className="dropdown-item"
                          onClick={() => {
                            console.log('🔘 Create Admin button clicked - setting admin form');
                            resetAdminForm();
                            setShowRegularUserForm(false);
                            setShowAdminForm(true);
                            setShowCreateDropdown(false);
                            setUserType('admin'); // Ensure user type is set to admin
                          }}
                        >
                          <span className="item-icon">👔</span>
                          Create Admin
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Designation</th>
                    <th>Event</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getDisplayList().map((user, index) => (
                    <tr key={`${user.id}-${index}`}>
                      <td>{user.name}</td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td>{user.address}</td>
                      <td>{user.designation?.name || '-'}</td>
                      <td>{user.event?.name || '-'}</td>
                      <td>
                        <span className={`status-badge ${user.status ? 'active' : 'inactive'}`}>
                          {user.status ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <button 
                          className="btn-edit"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDeleteUser(user.id, userListType === 'admins')}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* User Pagination Controls */}
            <div className="pagination-controls">
              <div className="pagination-info">
                <label>
                  Items per page:
                  <select 
                    value={userItemsPerPage} 
                    onChange={(e) => handleUserItemsPerPageChange(parseInt(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <span>Page {userCurrentPage} of {userTotalPages} (Total: {userTotalCount} items)</span>
              </div>
              
              <div className="pagination-buttons">
                <button 
                  className="btn-secondary"
                  onClick={() => handleUserPageChange(userCurrentPage - 1)}
                  disabled={userCurrentPage <= 1}
                >
                  Previous
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => handleUserPageChange(userCurrentPage + 1)}
                  disabled={userCurrentPage >= userTotalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'designations' && (
          <div className="designations-section">
            
            <div className="section-header">
              <h2>Designations Management</h2>
              <button 
                className="btn-primary"
                onClick={() => {
                  resetDesignationForm();
                  setShowDesignationForm(true);
                }}
              >
                Create Designation
              </button>
            </div>

            {/* Create/Edit Designation Form */}
            {showDesignationForm && (
              <div className="modal-overlay">
                <div className="modal role-modal">
                  <div className="modal-header">
                    <h3>{editingDesignation ? 'Edit Designation' : 'Create New Designation'}</h3>
                    <button className="modal-close" onClick={resetDesignationForm}>
                      ×
                    </button>
                  </div>
                  <form onSubmit={editingDesignation ? handleUpdateDesignation : handleCreateDesignation} className="designation-form">
                    <div className="modal-body">
                      <div className="form-group">
                        <label htmlFor="designationName">Designation Name</label>
                        <input
                          id="designationName"
                          type="text"
                          value={designationForm.name}
                          onChange={(e) => setDesignationForm({ ...designationForm, name: e.target.value })}
                          placeholder="e.g., Python, Software Engineer"
                          required
                          autoFocus
                        />
                        <small className="form-help">
                          Enter a descriptive name for this designation (e.g., Python, Software Engineer)
                        </small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="designationDescription">Description</label>
                        <textarea
                          id="designationDescription"
                          value={designationForm.description}
                          onChange={(e) => setDesignationForm({ ...designationForm, description: e.target.value })}
                          placeholder="Enter a description for this designation"
                          rows="4"
                        />
                        <small className="form-help text-muted">
                          Optional: Provide additional details about this designation
                        </small>
                      </div>
                    </div>

                    
                    <div className="modal-footer">
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={resetDesignationForm}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={loading || !designationForm.name.trim()}
                      >
                        {loading ? (
                          <span className="btn-spinner"></span>
                        ) : null}
                        {loading ? (editingDesignation ? 'Updating...' : 'Creating...') : (editingDesignation ? 'Update Designation' : 'Create Designation')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Designations List */}
            <div className="roles-grid">
              {designations.map(designation => (
                <div key={designation.id} className="role-card">
                  <div className="role-header">
                    <h3>{designation.name}</h3>
                    <div className="role-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEditDesignation(designation)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteDesignation(designation.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="role-meta">
                    <span className="role-id">ID: {designation.id}</span>
                    <span className={`role-status ${designation.status ? 'active' : 'inactive'}`}>
                      {designation.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {designation.description && (
                    <div className="role-details">
                      <div className="role-info">
                        <label>Description:</label>
                        <span>{designation.description}</span>
                      </div>
                    </div>
                  )}
                  <div className="role-details">
                    <div className="role-info">
                      <label>Created:</label>
                      <span>{formatDate(designation.created_at)}</span>
                    </div>
                    {designation.updated_at && (
                      <div className="role-info">
                        <label>Updated:</label>
                        <span>{formatDate(designation.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {designations.length === 0 && (
              <p className="no-roles">No designations found. Create your first designation above.</p>
            )}

            </div>
        )}

        {activeTab === 'events' && (
          <div className="events-section">
            
            <div className="section-header">
              <h2>Events Management</h2>
              <button 
                className="btn-primary"
                onClick={() => {
                  resetEventForm();
                  setShowEventForm(true);
                }}
              >
                Create Event
              </button>
            </div>

            {/* Create/Edit Event Form */}
            {showEventForm && (
              <div className="modal-overlay">
                <div className="modal role-modal">
                  <div className="modal-header">
                    <h3>{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
                    <button className="modal-close" onClick={resetEventForm}>
                      ×
                    </button>
                  </div>

                  
                  <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="event-form">
                    <div className="modal-body">
                      <div className="form-group">
                        <label htmlFor="eventName">Event Name</label>
                        <input
                          id="eventName"
                          type="text"
                          value={eventForm.name}
                          onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                          placeholder="e.g., Tech Conference 2026, Annual Meeting"
                          required
                          autoFocus
                        />
                        <small className="form-help text-muted">
                          Enter a descriptive name for this event (e.g., Tech Conference 2026, Annual Meeting)
                        </small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="eventDescription">Description</label>
                        <textarea
                          id="eventDescription"
                          value={eventForm.description}
                          onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                          placeholder="Enter a description for this event"
                          rows="4"
                        />
                        <small className="form-help text-muted">
                          Optional: Provide additional details about this event
                        </small>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={resetEventForm}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={loading || !eventForm.name.trim()}
                      >
                        {loading ? (
                          <span className="btn-spinner"></span>
                        ) : null}
                        {loading ? (editingEvent ? 'Updating...' : 'Creating...') : (editingEvent ? 'Update Event' : 'Create Event')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="roles-grid">
              {events.map(event => (
                <div key={event.id} className="role-card">
                  <div className="role-header">
                    <h3>{event.name}</h3>
                    <div className="role-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEditEvent(event)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteEvent(event.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="role-meta">
                    <span className="role-id">ID: {event.id}</span>
                    <span className={`role-status ${event.status ? 'active' : 'inactive'}`}>
                      {event.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {event.description && (
                    <div className="role-details">
                      <div className="role-info">
                        <label>Description:</label>
                        <span>{event.description}</span>
                      </div>
                    </div>
                  )}
                  <div className="role-details">
                    <div className="role-info">
                      <label>Created:</label>
                      <span>{formatDate(event.created_at)}</span>
                    </div>
                    {event.updated_at && (
                      <div className="role-info">
                        <label>Updated:</label>
                        <span>{formatDate(event.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {events.length === 0 && (
              <p className="no-roles">No events found. Create your first event above.</p>
            )}
          </div>
        )}

        {activeTab === 'builder' && (
          <div className="quizzes-section">
            {!showQuestionsView ? (
              <>
                <div className="section-header">
                  <h2>Quiz Management</h2>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setQuizModalMode('create');
                      setSelectedQuiz(null);
                      resetQuizForm();
                      setShowQuizForm(true);
                    }}
                  >
                    New Quiz
                  </button>
                </div>

                {/* Pagination Controls */}
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <label>
                      Items per page:
                      <select 
                        value={itemsPerPage} 
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </label>
                    <span>Page {currentPage} of {totalPages}</span>
                  </div>
                  
                  <div className="pagination-buttons">
                    <button 
                      className="btn-secondary"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      Previous
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="loading">
                    {/* Skeleton loading */}
                    <div className="quiz-table-container">
                      <table className="quiz-table">
                        <thead>
                          <tr>
                            <th>Quiz Title</th>
                            <th>Event Name</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Total Time</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...Array(5)].map((_, index) => (
                            <tr key={`skeleton-${index}`} className="skeleton-row">
                              <td><div className="skeleton"></div></td>
                              <td><div className="skeleton"></div></td>
                              <td><div className="skeleton"></div></td>
                              <td><div className="skeleton"></div></td>
                              <td><div className="skeleton"></div></td>
                              <td><div className="skeleton"></div></td>
                              <td><div className="skeleton"></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="quiz-table-container">
                    {/* Pagination info */}
                    <div className="pagination-info-top">
                      <span>Showing {quizzes.length} items on page {currentPage}</span>
                    </div>

                    <table className="quiz-table">
                      <thead>
                        <tr>
                          <th>Quiz Title</th>
                          <th>Event Name</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Total Time</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quizzes.map(quiz => (
                          <tr key={quiz.id}>
                            <td>{quiz.title}</td>
                            <td>{quiz.event?.name ?? '—'}</td>
                            <td>{formatDateTime(quiz.start_date)}</td>
                            <td>{formatDateTime(quiz.end_date)}</td>
                            <td>{formatTotalTime(quiz.total_time)}</td>
                            <td>
                              <span className={`status-badge ${quiz.status ? 'active' : 'inactive'}`}>
                                {quiz.status ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <button 
                                className="btn-primary btn-sm"
                                onClick={() => handleViewQuiz(quiz)}
                              >
                                View
                              </button>
                              <button 
                                className="btn-secondary btn-sm"
                                onClick={() => handleEditQuiz(quiz)}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn-danger btn-sm"
                                onClick={() => handleDeleteQuiz(quiz.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {quizzes.length === 0 && (
                      <div className="no-data">
                        <p>No quizzes found</p>
                        <button 
                          className="btn-primary"
                          onClick={() => {
                            setQuizModalMode('create');
                            setSelectedQuiz(null);
                            resetQuizForm();
                            setShowQuizForm(true);
                          }}
                        >
                          Create Quiz
                        </button>
                      </div>
                    )}

                    {/* Pagination controls */}
                    <div className="pagination-controls-bottom">
                      <div className="pagination-buttons">
                        <button 
                          className="btn-secondary"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                        >
                          Previous
                        </button>
                        <span>Page {currentPage}</span>
                        <button 
                          className="btn-secondary"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Questions View */
              <div className="questions-view">
                <div className="section-header">
                  <div className="quiz-header-info">
                    <button 
                      className="btn-secondary"
                      onClick={handleBackToQuizzes}
                    >
                      ← Back to Quizzes
                    </button>
                    <h2>{selectedQuiz?.title} - Questions</h2>
                    <p>{selectedQuiz?.description}</p>
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={() => setShowQuestionForm(true)}
                  >
                    Add Question
                  </button>
                </div>

                {loading ? (
                  <div className="loading">Loading questions...</div>
                ) : (
                  <div className="questions-table-container">
                    <table className="questions-table">
                      <thead>
                        <tr>
                          <th>Question Text</th>
                          <th>Type</th>
                          <th>Points</th>
                          <th>Time Limit</th>
                          <th>Options</th>
                          <th>Correct Answer</th>
                          <th>Designation</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quizQuestions.map(question => (
                          <tr key={question.id}>
                            <td>
                              <div className="question-cell-content">
                                <span>{question.question_text}</span>
                                {question.question_image_url && (
                                  <img
                                    className="question-table-image"
                                    src={getImageUrl(question.question_image_url)}
                                    alt="Question"
                                  />
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="question-type-badge">
                                {question.question_type}
                              </span>
                            </td>
                            <td>{question.points}</td>
                            <td>{formatTimeLimit(question.time_limit)}</td>
                            <td>
                              {question.options && question.options.length > 0 ? (
                                <ul className="options-list">
                                  {question.options.map((option, index) => (
                                    <li key={option.id || index}>
                                      <span>{option.option_text || `Image option ${index + 1}`}</span>
                                      {option.image_url && (
                                        <img
                                          className="option-table-image"
                                          src={getImageUrl(option.image_url)}
                                          alt={`Option ${index + 1}`}
                                        />
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="no-options">N/A</span>
                              )}
                            </td>
                            <td className="question-correct-answer-cell">
                              {renderCorrectAnswerDisplay(question.correct_answer, question.options, true)}
                            </td>
                            <td>
                              <span className="designation-badge">
                                {question.designation?.name || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${question.status ? 'active' : 'inactive'}`}>
                                {question.status ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button 
                                  className="btn-secondary btn-sm"
                                  onClick={() => handleEditQuestion(question)}
                                  title="Edit Question"
                                >
                                  ✏️
                                </button>
                                <button 
                                  className="btn-danger btn-sm"
                                  onClick={() => handleDeleteQuestion(question.id)}
                                  title="Delete Question"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {quizQuestions.length === 0 && (
                      <div className="no-data">
                        <p>No questions found for this quiz. Add your first question!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Quiz Form Modal */}
            {showQuizForm && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal-header">
                    <h3>{quizModalMode === 'edit' ? 'Edit Quiz' : 'Create New Quiz'}</h3>
                    <button 
                      className="modal-close"
                      onClick={handleQuizModalClose}
                    >
                      ×
                    </button>
                  </div>
                  <form onSubmit={handleCreateQuiz} className="quiz-form">
                    <div className="form-group">
                      <label>Quiz Title</label>
                      <input
                        name="title"
                        type="text"
                        value={quizForm.title}
                        onChange={(e) => setQuizForm({...quizForm, title: e.target.value})}
                        placeholder="e.g., Math Quiz"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        name="description"
                        value={quizForm.description}
                        onChange={(e) => setQuizForm({...quizForm, description: e.target.value})}
                        placeholder="e.g., A basic math quiz for grade 5"
                        rows="3"
                      />
                    </div>

                    <div className="form-group">
                      <label>Event</label>
                      <select
                        name="eventId"
                        value={quizForm.eventId || ''}
                        onChange={(e) => setQuizForm({...quizForm, eventId: parseInt(e.target.value)})}
                        required
                      >
                        <option value={0}>Select Event</option>
                        {availableQuizEvents.map(event => (
                          <option key={event.id} value={event.id}>
                            {event.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Start Date & Time</label>
                      <input
                        name="start_date"
                        type="datetime-local"
                        value={quizForm.start_date}
                        onChange={(e) => setQuizForm({...quizForm, start_date: e.target.value})}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>End Date & Time</label>
                      <input
                        name="end_date"
                        type="datetime-local"
                        value={quizForm.end_date}
                        onChange={(e) => setQuizForm({...quizForm, end_date: e.target.value})}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Total Time (minutes)</label>
                      <input
                        name="total_time"
                        type="number"
                        min="1"
                        max="600"
                        value={quizForm.total_time}
                        onChange={(e) => setQuizForm({...quizForm, total_time: parseInt(e.target.value) || 60})}
                        placeholder="60"
                        required
                      />
                      <small className="form-help">Example: 60 = 1 hour</small>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? (quizModalMode === 'edit' ? 'Updating...' : 'Creating...') : (quizModalMode === 'edit' ? 'Update Quiz' : 'Create Quiz')}
                      </button>
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={resetQuizForm}
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Question Form Modal */}
            {showQuestionForm && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal-header">
                    <h3>{isEditMode ? 'Edit Question' : 'Add Question'}</h3>
                    <button 
                      className="modal-close"
                      onClick={isEditMode ? cancelEditQuestion : () => {
                        resetQuestionForm();
                        setShowQuestionForm(false);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <form onSubmit={isEditMode ? handleUpdateQuestion : handleAddQuestion} className="question-form">
                    <div className="form-group">
                      <label>Question Text</label>
                      <textarea
                        value={questionForm.question_text}
                        onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                        placeholder="Enter your question"
                        rows="3"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Question Has Images?</label>
                      <div className="image-mode-toggle" role="group" aria-label="Question image mode">
                        <button
                          type="button"
                          className={!questionForm.has_images ? 'active' : ''}
                          onClick={() => setQuestionImageMode(false)}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          className={questionForm.has_images ? 'active' : ''}
                          onClick={() => setQuestionImageMode(true)}
                        >
                          Yes
                        </button>
                      </div>
                    </div>

                    {questionForm.has_images && (
                      <div className="form-group">
                        <label>Question Image</label>
                        <input
                          ref={questionImageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => updateQuestionImage(e.target.files?.[0] || null)}
                        />
                        {getQuestionImagePreview() && (
                          <div className="image-preview-row">
                            <img
                              className="question-image-preview"
                              src={getQuestionImagePreview()}
                              alt="Question preview"
                            />
                            <button
                              type="button"
                              className="btn-remove"
                              onClick={clearQuestionImage}
                            >
                              Remove Image
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="form-group">
                      <label>Question Type</label>
                      <select
                        value={questionForm.question_type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          setQuestionForm({
                            ...questionForm,
                            question_type: nextType,
                            correct_answer: '',
                            correct_answers: []
                          });
                        }}
                      >
                        <option value="radio">Multiple Choice (Single)</option>
                        <option value="checkbox">Multiple Choice (Multiple)</option>
                        <option value="text">Text Answer</option>
                        <option value="code">Code Answer</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Points</label>
                      <input
                        type="number"
                        value={questionForm.points}
                        onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})}
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Time Limit <span className="required-marker">*</span>
                      </label>
                      <div className="time-limit-presets">
                        <button
                          type="button"
                          className={`btn-secondary btn-sm ${normalizeTimeLimitInput(questionForm.time_limit) === '30s' ? 'active' : ''}`}
                          onClick={() => setQuestionForm({ ...questionForm, time_limit: '30s' })}
                        >
                          30s
                        </button>
                        <button
                          type="button"
                          className={`btn-secondary btn-sm ${normalizeTimeLimitInput(questionForm.time_limit) === '1m' ? 'active' : ''}`}
                          onClick={() => setQuestionForm({ ...questionForm, time_limit: '1m' })}
                        >
                          1m
                        </button>
                        <button
                          type="button"
                          className={`btn-secondary btn-sm ${normalizeTimeLimitInput(questionForm.time_limit) === '2m30s' ? 'active' : ''}`}
                          onClick={() => setQuestionForm({ ...questionForm, time_limit: '2m30s' })}
                        >
                          2m30s
                        </button>
                        <button
                          type="button"
                          className={`btn-secondary btn-sm ${normalizeTimeLimitInput(questionForm.time_limit) === '5m' ? 'active' : ''}`}
                          onClick={() => setQuestionForm({ ...questionForm, time_limit: '5m' })}
                        >
                          5m
                        </button>
                      </div>
                      <input
                        type="text"
                        value={questionForm.time_limit}
                        onChange={(e) => setQuestionForm({ ...questionForm, time_limit: e.target.value })}
                        onBlur={(e) => setQuestionForm({ ...questionForm, time_limit: normalizeTimeLimitInput(e.target.value) })}
                        placeholder="e.g., 20s, 1m, 2m30s"
                        required
                        aria-required="true"
                      />
                    </div>

                    <div className="form-group">
                      <label>Designation</label>
                      <select
                        value={questionForm.designationId || ''}
                        onChange={(e) => setQuestionForm({...questionForm, designationId: e.target.value ? parseInt(e.target.value) : null})}
                      >
                        <option value="">Select Designation</option>
                        {availableDesignations.map(designation => (
                          <option key={designation.id} value={designation.id}>
                            {designation.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Correct Answer</label>
                      {questionForm.question_type === 'radio' ? (
                        <div className="correct-answer-options">
                          {(questionForm.options || []).map((option, index) => {
                            const value = String(index);
                            return (
                              <label key={index} className="correct-answer-option">
                                <input
                                  type="radio"
                                  name="correct_answer"
                                  value={value}
                                  checked={(questionForm.correct_answer || '') === value}
                                  onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                                />
                                <span>{getOptionLabel(option, index)}</span>
                                {questionForm.has_images && getOptionImagePreview(option) && (
                                  <img
                                    className="correct-answer-option-image"
                                    src={getOptionImagePreview(option)}
                                    alt={`Option ${index + 1}`}
                                  />
                                )}
                              </label>
                            );
                          })}
                        </div>
                      ) : questionForm.question_type === 'checkbox' ? (
                        <div className="correct-answer-options">
                          {(questionForm.options || []).map((option, index) => {
                            const value = String(index);
                            const selected = Array.isArray(questionForm.correct_answers) ? questionForm.correct_answers : [];
                            const checked = selected.includes(value);
                            return (
                              <label key={index} className="correct-answer-option">
                                <input
                                  type="checkbox"
                                  value={value}
                                  checked={checked}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...selected, value]
                                      : selected.filter(v => v !== value);
                                    setQuestionForm({ ...questionForm, correct_answers: next });
                                  }}
                                />
                                <span>{getOptionLabel(option, index)}</span>
                                {questionForm.has_images && getOptionImagePreview(option) && (
                                  <img
                                    className="correct-answer-option-image"
                                    src={getOptionImagePreview(option)}
                                    alt={`Option ${index + 1}`}
                                  />
                                )}
                              </label>
                            );
                          })}
                        </div>
                      ) : questionForm.question_type === 'code' ? (
                        <textarea
                          value={questionForm.correct_answer}
                          onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                          placeholder="Enter correct code answer"
                          rows="4"
                          required
                        />
                      ) : (
                        <input
                          type="text"
                          value={questionForm.correct_answer}
                          onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                          placeholder="Enter correct answer"
                          required
                        />
                      )}
                    </div>

                    {(questionForm.question_type === 'radio' || questionForm.question_type === 'checkbox') && (
                      <div className="form-group">
                        <label>Options</label>
                        {questionForm.options.map((option, index) => (
                          <div key={index} className="option-input-group">
                            <div className="option-editor">
                              <input
                                type="text"
                                value={option.option_text}
                                onChange={(e) => updateOption(index, e.target.value)}
                                placeholder={`Option ${index + 1} text`}
                              />
                              {questionForm.has_images && (
                                <>
                                  <input
                                    ref={(input) => {
                                      if (input) {
                                        optionImageInputRefs.current[index] = input;
                                      } else {
                                        delete optionImageInputRefs.current[index];
                                      }
                                    }}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => updateOptionImage(index, e.target.files?.[0] || null)}
                                  />
                                  {getOptionImagePreview(option) && (
                                    <div className="image-preview-row">
                                      <img
                                        className="option-image-preview"
                                        src={getOptionImagePreview(option)}
                                        alt={`Option ${index + 1}`}
                                      />
                                      <button
                                        type="button"
                                        className="btn-remove"
                                        onClick={() => clearOptionImage(index)}
                                      >
                                        Remove Image
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                            {questionForm.options.length > 1 && (
                              <button 
                                type="button"
                                className="btn-remove"
                                onClick={() => removeOption(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button 
                          type="button"
                          className="btn-add"
                          onClick={addOption}
                        >
                          Add Option
                        </button>
                      </div>
                    )}

                    <div className="form-actions">
                      <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Question' : 'Add Question')}
                      </button>
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={isEditMode ? cancelEditQuestion : () => {
                          resetQuestionForm();
                          setShowQuestionForm(false);
                        }}
                      >
                        {isEditMode ? 'Cancel' : 'Clear'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'data' && (
          <div className="data-section">
            
            <div className="section-header">
              <h2>Quiz Data Management</h2>
              <button 
                className="btn-primary"
                onClick={() => setShowDataUpload(true)}
              >
                Upload Quiz Data
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading quizzes with questions...</div>
            ) : (
              <div className="quizzes-with-questions">
                {quizzesWithQuestions.map(quiz => (
                  <div key={quiz.id} className="quiz-with-questions">
                    <div className="quiz-header-info">
                      <h3>{quiz.title}</h3>
                      <p>{quiz.description}</p>
                      {quiz.event && (
                        <div className="quiz-event-info">
                          <strong>Event:</strong> {quiz.event.name}
                        </div>
                      )}
                    </div>
                    
                    <div className="questions-list">
                      <h4>Questions ({quiz.questions.length})</h4>
                      {quiz.questions.map(question => (
                        <div 
                          key={question.id} 
                          className="question-row"
                          onClick={() => handleQuestionClick(question)}
                        >
                          <div className="question-info">
                            <div className="question-text">
                              {question.question_text}
                            </div>
                            {question.question_image_url && (
                              <img
                                className="question-table-image"
                                src={getImageUrl(question.question_image_url)}
                                alt="Question"
                              />
                            )}
                            <div className="question-meta">
                              <span className="question-type">{question.question_type}</span>
                              <span className="question-points">{question.points} points</span>
                              <span className="correct-answer">
                                Answer: {renderCorrectAnswerDisplay(question.correct_answer, question.options, true)}
                              </span>
                            </div>
                          </div>
                          <div className="question-arrow">
                            →
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Question Details Modal */}
            {selectedQuestion && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal-header">
                    <h3>Question Details</h3>
                    <button 
                      className="modal-close"
                      onClick={closeQuestionDetails}
                    >
                      ×
                    </button>
                  </div>
                  <div className="question-details">
                    <div className="detail-group">
                      <label>Question Text:</label>
                      <p>{selectedQuestion.question_text}</p>
                    </div>

                    {selectedQuestion.question_image_url && (
                      <div className="detail-group">
                        <label>Question Image:</label>
                        <img
                          className="question-detail-image"
                          src={getImageUrl(selectedQuestion.question_image_url)}
                          alt="Question"
                        />
                      </div>
                    )}
                    
                    <div className="detail-group">
                      <label>Question Type:</label>
                      <span className="question-type-badge">{selectedQuestion.question_type}</span>
                    </div>
                    
                    <div className="detail-group">
                      <label>Points:</label>
                      <span className="points-badge">{selectedQuestion.points} points</span>
                    </div>
                    
                    <div className="detail-group">
                      <label>Correct Answer:</label>
                      <div className="correct-answer-text">
                        {renderCorrectAnswerDisplay(selectedQuestion.correct_answer, selectedQuestion.options)}
                      </div>
                    </div>
                    
                    {selectedQuestion.options && selectedQuestion.options.length > 0 && (
                      <div className="detail-group">
                        <label>Options:</label>
                        <div className="options-list">
                          {selectedQuestion.options.map((option, index) => (
                            <div key={option.id || index} className="option-item">
                              <span>{option.option_text || `Image option ${index + 1}`}</span>
                              {option.image_url && (
                                <img
                                  className="option-detail-image"
                                  src={getImageUrl(option.image_url)}
                                  alt={`Option ${index + 1}`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-actions">
                    <button 
                      className="btn-secondary"
                      onClick={closeQuestionDetails}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reviewer Tab */}
        {activeTab === 'reviewer' && (
          <div className="reviewer-section">
            <div className="section-header">
              <h2>Review Answer Sheets</h2>
              {reviewerView !== 'events' && (
                <div className="breadcrumb-nav">
                  {reviewerView === 'quizzes' && (
                    <>
                      <button className="breadcrumb-link" onClick={handleBackToEvents}>
                        Events
                      </button>
                      <span className="breadcrumb-separator">/</span>
                      <span className="breadcrumb-current">Quizzes for {selectedReviewerEvent?.name}</span>
                    </>
                  )}
                  {reviewerView === 'users' && (
                    <>
                      <button className="breadcrumb-link" onClick={handleBackToEvents}>
                        Events
                      </button>
                      <span className="breadcrumb-separator">/</span>
                      <button className="breadcrumb-link" onClick={handleBackToReviewerQuizzes}>
                        Quizzes
                      </button>
                      <span className="breadcrumb-separator">/</span>
                      <span className="breadcrumb-current">Users for {selectedReviewerQuiz?.title}</span>
                    </>
                  )}
                  {reviewerView === 'answers' && (
                    <>
                      <button className="breadcrumb-link" onClick={handleBackToEvents}>
                        Events
                      </button>
                      <span className="breadcrumb-separator">/</span>
                      <button className="breadcrumb-link" onClick={handleBackToReviewerQuizzes}>
                        Quizzes
                      </button>
                      <span className="breadcrumb-separator">/</span>
                      <button className="breadcrumb-link" onClick={handleBackToUsers}>
                        Users
                      </button>
                      <span className="breadcrumb-separator">/</span>
                      <span className="breadcrumb-current">Answers for {selectedReviewerUser?.user_name}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {reviewerLoading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading...</p>
              </div>
            )}

            {reviewerError && (
              <div className="error-state">
                <p>{reviewerError}</p>
                <button className="btn-primary" onClick={() => {
                  if (reviewerView === 'events') loadReviewerEvents();
                  else if (reviewerView === 'quizzes') loadReviewerQuizzes(selectedReviewerEvent.id);
                  else if (reviewerView === 'users') loadReviewerUsers(selectedReviewerQuiz.id, reviewerUsersPage, reviewerUsersLimit);
                  else if (reviewerView === 'answers') loadReviewerAnswers(selectedReviewerQuiz.id, selectedReviewerUser.user_id);
                }}>
                  Retry
                </button>
              </div>
            )}

            {!reviewerLoading && !reviewerError && (
              <>
                {/* Events List */}
                {reviewerView === 'events' && (
                  <div className="events-list">
                    {reviewerEvents.length === 0 ? (
                      <div className="empty-state">
                        <p>No events available for review</p>
                      </div>
                    ) : (
                      <div className="events-grid">
                        {reviewerEvents.map((event) => (
                          <div key={event.id} className="event-card">
                            <h3>{event.name}</h3>
                            <p>{event.description || 'No description'}</p>
                            <div className="event-stats">
                              <div className="stat">
                                <span className="stat-value">{event.quiz_count}</span>
                                <span className="stat-label">Quizzes</span>
                              </div>
                              <div className="stat">
                                <span className="stat-value">{event.submission_count}</span>
                                <span className="stat-label">Submissions</span>
                              </div>
                            </div>
                            <button 
                              className="btn-primary"
                              onClick={() => handleViewQuizzes(event)}
                            >
                              View Quizzes
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Quizzes List */}
                {reviewerView === 'quizzes' && (
                  <div className="quizzes-list">
                    {reviewerQuizzes.length === 0 ? (
                      <div className="empty-state">
                        <p>No quizzes found for this event</p>
                      </div>
                    ) : (
                      <div className="quizzes-grid">
                        {reviewerQuizzes.map((quiz) => (
                          <div key={quiz.id} className="quiz-card">
                            <h3>{quiz.title}</h3>
                            <p>{quiz.description || 'No description'}</p>
                            <div className="quiz-info">
                              <p><strong>Start Date:</strong> {formatDate(quiz.start_date)}</p>
                              <p><strong>Total Questions:</strong> {quiz.total_questions}</p>
                              <p><strong>Submitted Users:</strong> {quiz.submitted_users}</p>
                              <p><strong>Pending Review:</strong> {quiz.pending_review}</p>
                            </div>
                            <button 
                              className="btn-primary"
                              onClick={() => handleViewUsers(quiz)}
                            >
                              View Users
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Users List */}
                {reviewerView === 'users' && (
                  <div className="users-list">
                    {/* Users List Header */}
                    <div className="users-list-header">
                      <h3>Users for {selectedReviewerQuiz?.title}</h3>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          Designation:
                          <select
                            value={reviewerUsersDesignationFilter}
                            onChange={(e) => setReviewerUsersDesignationFilter(e.target.value)}
                          >
                            <option value="all">All</option>
                            {[...new Set(reviewerUsers.map((u) => (u?.designation || 'Unknown')))].map((d) => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      {selectedReviewerEvent && (
                        <button 
                          className="btn-primary"
                          onClick={() => handleExport(selectedReviewerEvent.id)}
                          disabled={exporting}
                        >
                          {exporting ? 'Exporting...' : 'Export Excel'}
                        </button>
                      )}
                    </div>
                    {reviewerUsers.length === 0 ? (
                      <div className="empty-state">
                        <p>No submissions for this quiz</p>
                      </div>
                    ) : (
                      <>
                      <div className="users-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Designation</th>
                              <th>Score</th>
                              <th>Progress</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getReviewerFilteredUsers().length === 0 ? (
                              <tr>
                                <td colSpan={7} style={{ textAlign: 'center' }}>
                                  No users match the selected designation
                                </td>
                              </tr>
                            ) : getReviewerFilteredUsers().map((user) => (
                              <tr key={user.user_id}>
                                <td>{user.user_name}</td>
                                <td>{user.user_email}</td>
                                <td>{user.designation}</td>
                                <td>{user.total_points_earned} / {user.total_points}</td>
                                <td>{user.answered_questions} / {user.total_questions}</td>
                                <td>
                                  <span className={`status-badge ${user.has_pending_review ? 'pending' : 'reviewed'}`}>
                                    {user.has_pending_review ? 'Pending Review' : 'Reviewed'}
                                  </span>
                                </td>
                                <td>
                                  <button 
                                    className="btn-primary"
                                    onClick={() => handleViewAnswers(user)}
                                  >
                                    Review Answers
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="pagination-controls">
                        <div className="pagination-info">
                          <label>
                            Items per page:
                            <select
                              value={reviewerUsersLimit}
                              onChange={(e) => handleReviewerUsersLimitChange(parseInt(e.target.value))}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </label>
                          <span>Page {reviewerUsersPage} of {reviewerUsersTotalPages} (Total: {reviewerUsersTotalCount} items, Showing: {getReviewerFilteredUsers().length})</span>
                        </div>

                        <div className="pagination-buttons">
                          <button
                            className="btn-secondary"
                            onClick={() => handleReviewerUsersPageChange(reviewerUsersPage - 1)}
                            disabled={reviewerUsersPage <= 1}
                          >
                            Previous
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => handleReviewerUsersPageChange(reviewerUsersPage + 1)}
                            disabled={reviewerUsersPage >= reviewerUsersTotalPages}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                      </>
                    )}
                  </div>
                )}

                {/* Answers Review */}
                {reviewerView === 'answers' && (
                  <div className="answers-review">
                    {/* All Answers Reviewed Banner */}
                    {reviewerAnswers.length > 0 && reviewerAnswers.filter(a => !a.is_graded).length === 0 && (
                      <div className="all-reviewed-banner">
                        <div className="banner-content">
                          <h3>🎉 All answers reviewed</h3>
                          <p>All answers for this user have been graded successfully.</p>
                          <button 
                            className="btn-primary"
                            onClick={handleBackToUsers}
                          >
                            Back to Users
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="answers-header">
                      <div className="answers-summary">
                        <h3>Summary</h3>
                        <div className="summary-stats">
                          <div className="summary-stat">
                            <span className="stat-value">{reviewerAnswers.length}</span>
                            <span className="stat-label">Total Questions</span>
                          </div>
                          <div className="summary-stat">
                            <span className="stat-value">{reviewerAnswers.filter(a => a.is_graded).length}</span>
                            <span className="stat-label">Graded</span>
                          </div>
                          <div className="summary-stat">
                            <span className="stat-value">{reviewerAnswers.filter(a => !a.is_graded).length}</span>
                            <span className="stat-label">Pending</span>
                          </div>
                        </div>
                      </div>
                      <div className="answers-filter">
                        <label>Filter:</label>
                        <select
                          value={answerFilter}
                          onChange={(e) => setAnswerFilter(e.target.value)}
                          className="filter-select"
                        >
                          <option value="all">All</option>
                          <option value="pending">Pending only</option>
                          <option value="graded">Graded only</option>
                        </select>
                      </div>
                    </div>

                    {getFilteredAnswers().length === 0 ? (
                      <div className="empty-state">
                        <p>No answers found for the selected filter</p>
                      </div>
                    ) : (
                      <div className="answers-list">
                        {getFilteredAnswers().map((answer, index) => {
                          // Create a unique key even if answer_sheet_id is null
                          const uniqueKey = getAnswerId(answer) || `answer-${answer.question_id}-${index}`;
                          return (
                          <div key={uniqueKey} className="answer-card">
                            <div className="answer-header">
                              <div className="question-info">
                                <h4>Question #{index + 1}</h4>
                                <span className={`question-type-badge ${answer.question_type}`}>
                                  {answer.question_type}
                                </span>
                              </div>
                              <div className="points-info">
                                <span className="points-awarded">{answer.points_awarded}</span>
                                <span className="points-separator">/</span>
                                <span className="points-possible">{answer.points_possible}</span>
                              </div>
                              <div className="grading-status">
                                <span className={`status-badge ${answer.is_graded ? 'graded' : 'pending'}`}>
                                  {answer.is_graded ? 'Graded' : 'Pending Manual Review'}
                                </span>
                              </div>
                            </div>

                            <div className="question-content">
                              <h5>Question:</h5>
                              <div className="question-text">
                                {answer.question_type === 'code' ? (
                                  <pre><code>{answer.question_text}</code></pre>
                                ) : (
                                  <p>{answer.question_text}</p>
                                )}
                                {answer.question_image_url && (
                                  <img
                                    className="question-detail-image"
                                    src={getImageUrl(answer.question_image_url)}
                                    alt="Question"
                                  />
                                )}
                              </div>
                            </div>

                            <div className="user-answer">
                              <h5>User Answer:</h5>
                              {answer.question_type === 'radio' && (
                                <div className="radio-answer">
                                  <p>Selected: {getSelectedOptionSummary(answer)}</p>
                                  {answer.options && (
                                    <div className="options-list">
                                      {answer.options.map((option, optionIndex) => (
                                        <div 
                                          key={option.id} 
                                          className={`option ${answer.selected_options?.some(so => so.id === option.id) ? 'selected' : ''}`}
                                        >
                                          <span>{getAnswerOptionLabel(option, optionIndex)}</span>
                                          {option.image_url && (
                                            <img
                                              className="option-detail-image"
                                              src={getImageUrl(option.image_url)}
                                              alt={getAnswerOptionLabel(option, optionIndex)}
                                            />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {answer.question_type === 'checkbox' && (
                                <div className="checkbox-answer">
                                  <p>Selected: {getSelectedOptionSummary(answer)}</p>
                                  {answer.options && (
                                    <div className="options-list">
                                      {answer.options.map((option, optionIndex) => (
                                        <div 
                                          key={option.id} 
                                          className={`option ${answer.selected_options?.some(so => so.id === option.id) ? 'selected' : ''}`}
                                        >
                                          <span>{getAnswerOptionLabel(option, optionIndex)}</span>
                                          {option.image_url && (
                                            <img
                                              className="option-detail-image"
                                              src={getImageUrl(option.image_url)}
                                              alt={getAnswerOptionLabel(option, optionIndex)}
                                            />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {answer.question_type === 'text' && (
                                <div className="text-answer">
                                  <p>{answer.answer_text || 'No answer provided'}</p>
                                </div>
                              )}

                              {answer.question_type === 'code' && (
                                <div className="code-answer">
                                  {console.log('🔍 Rendering code answer:', { question_type: answer.question_type, code_language: answer.code_language })}
                                  <div className="code-language-selector">
                                    <label>Language:</label>
                                    <select 
                                      className="language-dropdown"
                                      value={selectedLanguages[getAnswerId(answer)] || answer.code_language || ''}
                                      onChange={(e) => {
                                        const answerId = getAnswerId(answer);
                                        setSelectedLanguages(prev => ({
                                          ...prev,
                                          [answerId]: e.target.value
                                        }));
                                      }}
                                    >
                                      <option value="">Select Language</option>
                                      {availableLanguages.map(lang => (
                                        <option key={lang.value} value={lang.value}>
                                          {lang.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <pre><code>{answer.answer_text || 'No answer provided'}</code></pre>
                                  <button 
                                    className="btn-run-code"
                                    onClick={async () => {
                                      console.log('Run Code clicked for answer:', answer);
                                      
                                      try {
                                        const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN) || localStorage.getItem('access_token');
                                        console.log('🔍 Compiler token:', token ? 'exists' : 'missing');
                                        const selectedLanguage = selectedLanguages[getAnswerId(answer)] || answer.code_language || '';
                                        const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.COMPILER_EXECUTE), {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${token}`,
                                            'accept': '*/*'
                                          },
                                          body: JSON.stringify({
                                            code: answer.answer_text,
                                            language: selectedLanguage,
                                            stdinInput: ''
                                          })
                                        });

                                        const result = await response.json();
                                        console.log('Code execution result:', result);

                                        if (result.status) {
                                          alert(`Output:\n${result.data.stdout}${result.data.stderr ? '\nErrors:\n' + result.data.stderr : ''}`);
                                        } else {
                                          alert('Error executing code: ' + (result.message || 'Unknown error'));
                                        }
                                      } catch (error) {
                                        console.error('Error executing code:', error);
                                        alert('Error executing code: ' + error.message);
                                      }
                                    }}
                                  >
            Run Code
          </button>
                                </div>
                              )}
                            </div>

                            <div className="correct-answer">
                              <h5>Correct Answer:</h5>
                              <div className="correct-answer-text">
                                {renderCorrectAnswerDisplay(answer.correct_answer, answer.options)}
                              </div>
                            </div>

                           

                            {answer.reviewer && (
  <div className="reviewer-info">
    <p>
      <strong>Reviewed by:</strong>{" "}
      {typeof answer.reviewer === "object"
        ? (answer.reviewer.name ?? answer.reviewer.id ?? "Reviewer")
        : answer.reviewer}
    </p>

    {answer.reviewer_notes && (
      <p><strong>Notes:</strong> {answer.reviewer_notes}</p>
    )}
  </div>
)}

                            {/* Grading Section for Pending Answers */}
                            {!answer.is_graded && (
                              <div className="grading-section">
                                {(() => {
                                  const hasProperId = answer.id || answer.answer_id || answer.sheet_id || answer.answer_sheet_id;
                                  console.log('🔍 Checking answer for grading:', {
                                    answer: answer,
                                    hasProperId: hasProperId,
                                    answer_sheet_id: answer.answer_sheet_id,
                                    id: answer.id,
                                    answer_id: answer.answer_id,
                                    sheet_id: answer.sheet_id
                                  });
                                  
                                  // if (!hasProperId) {
                                  //   console.log('🔍 Showing disabled message for answer:', answer);
                                  //   return (
                                  //     <div className="grading-disabled">
                                  //       <p className="grading-disabled-message">
                                  //         ⚠️ This answer cannot be graded because it has no valid identifier.
                                  //         Please contact the administrator to resolve this issue.
                                  //       </p>
                                  //     </div>
                                  //   );
                                  // }
                                  
                                 // console.log('🔍 Showing grading fields for answer with ID:', hasProperId);
                                  return (
                                    <>
                                      <h5>Grading:</h5>
                                      <div className="grading-inputs">
                                        <div className="grading-input-group">
                                          <label>Points:</label>
                                          <input
                                            type="number"
                                            min="0"
                                            max={answer.points_possible}
                                            step="0.5"
                                            value={gradingInputs[getAnswerId(answer)] || ''}
                                            onChange={(e) => handlePointsChange(getAnswerId(answer), e.target.value)}
                                            className="points-input"
                                            disabled={gradingAnswers[getAnswerId(answer)]}
                                          />
                                          <span className="points-max">/ {answer.points_possible}</span>
                                        </div>
                                        <div className="grading-input-group">
                                          <label>Notes (optional):</label>
                                          <textarea
                                            value={gradingNotes[getAnswerId(answer)] || ''}
                                            onChange={(e) => handleNotesChange(getAnswerId(answer), e.target.value)}
                                            className="notes-input"
                                            rows="3"
                                            placeholder="Add grading notes..."
                                            disabled={gradingAnswers[getAnswerId(answer)]}
                                          />
                                        </div>
                                      </div>
                                      <div className="grading-actions">
                                        <button
                                          className="btn-primary"
                                          onClick={() => {
                                            const answerId = getAnswerId(answer);
                                            console.log('🔍 Grade button clicked for answer:', answer);
                                            console.log('🔍 Using answerId for grading:', answerId);
                                            
                                            handleGradeAnswer(answerId);
                                          }}
                                          disabled={gradingAnswers[getAnswerId(answer)]}
                                        >
                                          {gradingAnswers[getAnswerId(answer)] ? (
                                            <>
                                              <span className="btn-spinner"></span>
                                              Saving...
                                            </>
                                          ) : (
                                            'Save Grade'
                                          )}
                                        </button>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* User Results Tab */}
        {activeTab === 'userResults' && (
          <div className="user-results-section">
            <div className="user-results-layout">
              {/* Left Panel - Events List */}
              <div className="events-panel">
                <div className="panel-header">
                  <h3>Events</h3>
                </div>
                
                {userResultsLoading && (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading events...</p>
                  </div>
                )}

                {userResultsError && (
                  <div className="error-state">
                    <p>{userResultsError}</p>
                    <button className="btn-primary" onClick={() => loadUserResultsEvents()}>
                      Retry
                    </button>
                  </div>
                )}

                {!userResultsLoading && !userResultsError && (
                  <>
                    {userResultsEvents.length === 0 ? (
                      <div className="empty-state">
                        <p>No events found</p>
                      </div>
                    ) : (
                      <div className="events-list-compact">
                        {userResultsEvents.map((event) => (
                          <div 
                            key={event.id} 
                            className={`event-item ${selectedUserResultsEvent?.id === event.id ? 'selected' : ''}`}
                            onClick={() => handleSelectEvent(event)}
                          >
                            <h4>{event.name}</h4>
                            {event.description && (
                              <p>{event.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Events Pagination */}
                    {eventsTotal > eventsLimit && (
                      <div className="pagination-controls">
                        <button 
                          className="btn-pagination"
                          onClick={() => handleEventsPageChange(eventsPage - 1)}
                          disabled={eventsPage <= 1 || userResultsLoading}
                        >
                          ← Previous
                        </button>
                        <span className="pagination-info">
                          Page {eventsPage} of {Math.ceil(eventsTotal / eventsLimit)}
                        </span>
                        <button 
                          className="btn-pagination"
                          onClick={() => handleEventsPageChange(eventsPage + 1)}
                          disabled={eventsPage >= Math.ceil(eventsTotal / eventsLimit) || userResultsLoading}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Right Panel - Leaderboard */}
              <div className="leaderboard-panel">
                {selectedUserResultsEvent ? (
                  <>
                    <div className="panel-header">
                      <h3>Leaderboard - {selectedUserResultsEvent.name}</h3>
                    </div>

                    {userResultsLoading && (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading leaderboard...</p>
                      </div>
                    )}

                    {userResultsError && (
                      <div className="error-state">
                        <p>{userResultsError}</p>
                        <button className="btn-primary" onClick={() => loadEventLeaderboard(selectedUserResultsEvent.id)}>
                          Retry
                        </button>
                      </div>
                    )}

                    {!userResultsLoading && !userResultsError && (
                      <>
                        {userResultsLeaderboard.length === 0 ? (
                          <div className="empty-state">
                            <p>No user results for this event yet.</p>
                          </div>
                        ) : (
                          <div className="leaderboard-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Rank</th>
                                  <th>User</th>
                                  <th>Email</th>
                                  <th>Phone</th>
                                  <th>Designation</th>
                                  <th>Total Points</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userResultsLeaderboard.map((user, index) => (
                                  <tr key={user.user_id}>
                                    <td className="rank-cell">
                                      <span className="rank-badge">#{calculateRank(index)}</span>
                                    </td>
                                    <td className="user-cell">
                                      <div className="user-info">
                                        {user.user_avatar && (
                                          <img 
                                            src={user.user_avatar} 
                                            alt={user.user_name}
                                            className="user-avatar"
                                          />
                                        )}
                                        <div className="user-details">
                                          <div className="user-name">{user.user_name}</div>
                                          <div className="user-username">@{user.user_username}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>{user.user_email}</td>
                                    <td>{user.user_phone || '-'}</td>
                                    <td>
                                      <span className="designation-badge">
                                        {user.designation?.name || '-'}
                                      </span>
                                    </td>
                                    <td className="points-cell">
                                      <span className="total-points">
                                        {user.total_points_earned ?? 0} / {user.total_points ?? 0}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Leaderboard Pagination */}
                        {leaderboardTotal > 0 && (
                          <div className="pagination-controls">
                            <div className="pagination-info">
                              <label>
                                Items per page:
                                <select 
                                  value={leaderboardLimit} 
                                  onChange={(e) => handleLeaderboardItemsPerPageChange(parseInt(e.target.value))}
                                >
                                  <option value={10}>10</option>
                                  <option value={20}>20</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                </select>
                              </label>
                              <span>Page {leaderboardPage} of {Math.ceil(leaderboardTotal / leaderboardLimit)} (Total: {leaderboardTotal} items)</span>
                            </div>
                            
                            <div className="pagination-buttons">
                              <button 
                                className="btn-pagination"
                                onClick={() => handleLeaderboardPageChange(leaderboardPage - 1)}
                                disabled={leaderboardPage <= 1 || userResultsLoading}
                              >
                                ← Previous
                              </button>
                              <button 
                                className="btn-pagination"
                                onClick={() => handleLeaderboardPageChange(leaderboardPage + 1)}
                                disabled={leaderboardPage >= Math.ceil(leaderboardTotal / leaderboardLimit) || userResultsLoading}
                              >
                                Next →
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="select-event-prompt">
                    <div className="prompt-content">
                      <h3>Select an Event</h3>
                      <p>Choose an event from the left panel to view the leaderboard.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Admin Form Modal */}
      {showAdminForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingUser ? 'Edit Admin' : 'Create Admin'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAdminForm(false);
                  resetAdminForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateAdmin} className="user-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., John Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username || ''}
                  onChange={(e) => {
                    console.log('Admin username changed to:', e.target.value);
                    setFormData({...formData, username: e.target.value});
                  }}
                  placeholder="e.g., johnsmith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="e.g., +1234567890"
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="e.g., john.smith@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter a strong password"
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Avatar URL</label>
                <input
                  type="url"
                  value={formData.avatar || ''}
                  onChange={(e) => setFormData({...formData, avatar: e.target.value})}
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={formData.address || ''}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="e.g., 123 Main St, City, State 12345"
                  rows="3"
                />
              </div>
              {/* Admins don't use Designation and Event fields - they use roleId: 0 */}
              {/* <div className="form-group">
                <label>Designation</label>
                <select
                  value={formData.designationId || ''}
                  onChange={(e) => setFormData({...formData, designationId: parseInt(e.target.value)})}
                  required
                >
                  <option value={0}>Select Designation</option>
                  {availableDesignations.map(designation => (
                    <option key={designation.id} value={designation.id}>
                      {designation.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Event</label>
                <select
                  value={formData.eventId || ''}
                  onChange={(e) => setFormData({...formData, eventId: parseInt(e.target.value)})}
                  required
                >
                  <option value={0}>Select Event</option>
                  {availableEvents.map(event => (
                    <option key={event.id} value={event.id}>
                      {event.name}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update Admin' : 'Create Admin')}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowAdminForm(false);
                    resetAdminForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {showRegularUserForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Create User'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowRegularUserForm(false);
                  resetUserForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateRegularUser} className="user-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Jane Doe"
                  required
                />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    console.log('User username changed to:', e.target.value);
                    setFormData({...formData, username: e.target.value});
                  }}
                  placeholder="e.g., janedoe"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="e.g., +0987654321"
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="e.g., jane.doe@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Create a secure password"
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Avatar URL</label>
                <input
                  type="url"
                  value={formData.avatar}
                  onChange={(e) => setFormData({...formData, avatar: e.target.value})}
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="e.g., 456 Oak Ave, City, State 67890"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Designation</label>
                <select
                  value={formData.designationId || ''}
                  onChange={(e) => setFormData({...formData, designationId: parseInt(e.target.value)})}
                  required
                >
                  <option value={0}>Select Designation</option>
                  {availableDesignations.map(designation => (
                    <option key={designation.id} value={designation.id}>
                      {designation.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Event</label>
                <select
                  value={formData.eventId || ''}
                  onChange={(e) => setFormData({...formData, eventId: parseInt(e.target.value)})}
                  required
                >
                  <option value={0}>Select Event</option>
                  {availableEvents.map(event => (
                    <option key={event.id} value={event.id}>
                      {event.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowRegularUserForm(false);
                    resetUserForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Upload Modal */}
      {showDataUpload && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload Quiz Data</h3>
              <button 
                className="modal-close"
                onClick={() => setShowDataUpload(false)}
              >
                ×
              </button>
            </div>
            <div className="upload-form">
              <div className="form-group">
                <label>Select JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="file-input"
                />
              </div>
              <div className="upload-info">
                <h4>Expected JSON Format:</h4>
                <pre>{`{
  "title": "Quiz Title",
  "description": "Quiz Description",
  "questions": [
    {
      "id": 1,
      "type": "radio",
      "question": "Question text",
      "options": ["Option 1", "Option 2"],
      "correct": "Option 1",
      "points": 10
    }
  ]
}`}</pre>
              </div>
              <div className="form-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowDataUpload(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Bulk Upload Users</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setBulkUploadFile(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="bulkFile">Select Excel File (.xlsx)</label>
                <input
                  type="file"
                  id="bulkFile"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                {bulkUploadFile && (
                  <div className="selected-file">
                    <strong>Selected file:</strong> {bulkUploadFile.name}
                    <br />
                    <small>Size: {(bulkUploadFile.size / 1024 / 1024).toFixed(2)} MB</small>
                  </div>
                )}
              </div>
              
              <div className="bulk-upload-info">
                <h4>Required Headers:</h4>
                <p>The Excel file must contain the following columns (headers must match exactly):</p>
                <ul>
                  <li><strong>name</strong> - User's full name</li>
                  <li><strong>email</strong> - User's email address</li>
                  <li><strong>username</strong> - Unique username</li>
                  <li><strong>phone</strong> - Phone number (optional)</li>
                  <li><strong>address</strong> - Address (optional)</li>
                  <li><strong>designation</strong> - Designation name (must match existing designation)</li>
                  <li><strong>event</strong> - Event name (must match existing event)</li>
                </ul>
                
                <div className="template-download">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={downloadTemplate}
                  >
                    Download Template
                  </button>
                  <small>Download a template file with the correct headers</small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setBulkUploadFile(null);
                }}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-primary"
                onClick={() => {
                  console.log('Upload button clicked');
                  console.log('isUploading:', isUploading);
                  console.log('bulkUploadFile:', bulkUploadFile);
                  handleBulkUpload();
                }}
                disabled={isUploading || !bulkUploadFile}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <DeleteConfirm
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm(prev => ({ ...prev, isOpen: false }))}
        onConfirm={deleteConfirm.onConfirm}
        entityType={deleteConfirm.entityType}
        entityLabel={deleteConfirm.entityLabel}
        impactWarnings={deleteConfirm.impactWarnings}
        isDeleting={deleteConfirm.isDeleting}
      />
    </div>
  );
};

export default AdminDashboard;
